/**
 * RETIRED TownDef compatibility model.
 *
 * Live terrain, collision, resources, rendering, NPCs, and quests consume the
 * region-native worldgen settlement stack. These deterministic helpers remain for
 * direct legacy fixtures only and do not mutate PlanetSampler.sample().
 *
 * Three historical layers:
 *  - `buildTowns(sampler)` → `TownField`: the per-planet registry. Sites are
 *    picked by biome (above sea, off rivers, low relief, temperate-ish) with
 *    a coastal-plains bias, sized by archetype (terran worlds grow cities,
 *    barren worlds at most an outpost).
 *  - `TownField.padAt(dir)`: the former terrain-pad query (no live consumer).
 *  - `townLayout(town)`: the lazily-built street/lot plan — plaza, ring
 *    streets, building footprints in the town's tangent frame. SHARED so the
 *    old geometry fixtures can still reproduce the retired model.
 *
 * Frames: `TownDef.dir` is a planet-local unit direction. A town's layout
 * lives in the tangent plane at `dir` — axes from `townBasis(dir)`, local
 * (u, v) in meters. Pads are small relative to the planet (≤ ~250 m on a
 * ≥ 8 km radius body), so the tangent-plane approximation holds.
 */
// NOTE the runtime import from universe.js is a deliberate ESM cycle
// (universe.ts imports buildTowns from here). It is safe because neither
// module touches the other's bindings during evaluation — PlanetSampler is
// only constructed at call time, long after both modules finish loading.
import { PlanetSampler, type PlanetDef, type TerrainSample } from "./universe.js";
import * as pmath from "./pmath.js";
import { rngFromString, range, pick, type Rng } from "./rng.js";
import { settlementName } from "./worldgen/settlementNames.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TownTier = "outpost" | "hamlet" | "village" | "town" | "city";

export interface TownDef {
  /** Index in the planet's town list. */
  id: number;
  /** `${planet.seed}:town:${id}` — sub-seeds layout, names, (later) NPCs. */
  seed: string;
  name: string;
  /** Site center: planet-local unit direction. */
  dir: [number, number, number];
  tier: TownTier;
  /** Flat pad core radius (m) — buildings live inside ~90% of this. */
  padR: number;
  /** Outer apron radius (m) — the pad eases back into wild terrain here. */
  apronR: number;
  /** Pad elevation at the town CENTER in AMPLITUDE units (pre-town level). */
  padH: number;
  /**
   * Tangent-plane curvature term (amplitude units per m²): the pad surface
   * is padH + dist²·sag — the PLANE the rigid town meshes live on, not a
   * constant-radius spherical cap, which would leave outskirt buildings
   * floating ~d²/2R above the ground.
   */
  sag: number;
  /** Climate at the site — flavors palettes, species, (later) NPC dress. */
  biome: { temp: number; moist: number };
  /** Precomputed dot-product cutoff for the sample() hot loop. */
  cosApron: number;
  /** Precomputed tangent basis (padAt is hot — no per-call cross products). */
  t1: [number, number, number];
  t2: [number, number, number];
  /**
   * ORGANIC BOUNDARY — the pad is a lobed radial function, not a circle
   * (the perfect disc is the single loudest "an algorithm made this" tell).
   * radius(θ) = padR · (1 + Σ warpAmp[i]·cos((i+2)θ + warpPh[i]) + fingers),
   * where each finger is a gaussian bulge along an exit road.
   */
  warpAmp: number[];
  warpPh: number[];
  fingers: { a: number; f: number; s: number }[];
  /** Working-landscape belt width (m) beyond the built edge — strip fields,
   *  orchards, farmsteads. The pad stays flat through it. 0 = none. */
  farmW: number;
  /** Pad→wild blend width (m) beyond the farm belt. */
  blendW: number;
}

/** The lobed built-edge radius (m) of a settlement at tangent angle θ. */
export function townRadiusAt(t: TownDef, th: number): number {
  let w = 1;
  for (let i = 0; i < t.warpAmp.length; i++) {
    w += t.warpAmp[i] * pmath.cos((i + 2) * th + t.warpPh[i]);
  }
  for (const f of t.fingers) {
    let da = th - f.a;
    da -= Math.round(da / (Math.PI * 2)) * (Math.PI * 2);
    w += f.f * pmath.exp(-(da * da) / (2 * f.s * f.s));
  }
  return t.padR * Math.max(0.55, w);
}

/** Max radius multiplier a town's warp can reach (containment budgets). */
export function townMaxWarp(t: TownDef): number {
  let w = 1;
  for (const a of t.warpAmp) w += Math.abs(a);
  for (const f of t.fingers) w += Math.max(0, f.f);
  return w;
}

/** Result of a pad query: blend factor + target level. */
export interface TownPad {
  /** 1 inside the flat core → 0 at the apron edge. */
  k: number;
  /** Pad level, amplitude units. */
  h: number;
  town: TownDef;
  /** Distance from the town center, meters. */
  dist: number;
  /** The lobed BUILT edge radius at this bearing (m). */
  coreR: number;
  /** The outer working-landscape edge at this bearing (built + farm belt). */
  farmR: number;
}

export interface BuildingDef {
  /** Footprint center in town-local meters (u along basis t1, v along t2). */
  u: number;
  v: number;
  /** Rotation about local up; 0 = building x-axis along t1. */
  yaw: number;
  /** Full footprint extents (m): w along the building x-axis, d along z. */
  w: number;
  d: number;
  /** Wall height (m) above the pad. */
  h: number;
  /** Roof rise above the walls (m) — 0-ish for flat roofs. */
  roofH: number;
  kind: BuildingKind;
  /** Roof profile: pitched hip roof, or flat (towers/warehouses/civic). */
  roof: "pitched" | "flat";
  /** Wall palette index (0 warm / 1 pale / 2 stone) — district flavor. */
  tint: number;
  /** Per-building visual variation seed. */
  seed: number;
}

export type BuildingKind =
  | "dwelling"
  | "hall"
  | "market"
  | "tower"
  | "warehouse"
  | "civic"
  | "spire"
  | "dome"
  | "wall"
  | "gate"
  | "windmill"
  | "barn";

/** Flat tinted ground patch (fields, gardens, graveyard gravel). */
export interface GroundPatch {
  u: number;
  v: number;
  yaw: number;
  w: number;
  d: number;
  /** 0-2 crops · 3 fallow · 4 garden green · 5 gravel. */
  tint: number;
}

/** Renderer-only decoration (no collision). The `board` kiosk is the C3 quest
 *  board — one per village+ settlement at the plaza rim (see townBoardPos). */
export interface TownProp {
  kind: "tree" | "slab" | "well" | "stall" | "board";
  u: number;
  v: number;
  s: number;
  yaw: number;
}

/** A straight street ribbon (town-local meters). */
export interface StreetSeg {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  w: number;
}

export interface TownLayout {
  plazaR: number;
  /** Plaza center (grid cities sit it off-axis; 0,0 elsewhere). */
  plazaU: number;
  plazaV: number;
  streetW: number;
  /** Street plan archetype — the macro identity of the settlement. */
  plan: "rings" | "grid" | "radial" | "hybrid" | "twin";
  /** Spoke street angles (radians in the u/v plane) — radial plans. */
  spokes: number[];
  /** Ring street radii (m) — radial plans. */
  rings: number[];
  /** Explicit straight streets — grid plans / boulevards. */
  streets: StreetSeg[];
  /** Open green blocks — deliberate negative space. */
  parks: { u: number; v: number; r: number }[];
  /** Working-landscape ground: strip fields, gardens, graveyard gravel. */
  patches: GroundPatch[];
  /** Renderer-only decorations: orchard trees, grave slabs, wells. */
  props: TownProp[];
  /** Exit-road azimuths whose stubs continue beyond the pad (drape client-side). */
  stubs: number[];
  buildings: BuildingDef[];
  /** Lamp post positions (town-local meters). */
  lamps: { u: number; v: number }[];
  /** Tallest wall+roof (m) — quick vertical reject for collision. */
  maxH: number;
  /** Uniform spatial hash over footprints (collision/occlusion queries). */
  cellSize: number;
  cells: Map<number, number[]>;
}

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

interface TierSpec {
  padR: number;
  plazaR: number;
  spokes: number;
  density: number;
  label: string;
}

export const TOWN_TIERS: Record<TownTier, TierSpec> = {
  outpost: { padR: 34, plazaR: 6, spokes: 0, density: 0.6, label: "OUTPOST" },
  hamlet: { padR: 55, plazaR: 8, spokes: 2, density: 0.55, label: "HAMLET" },
  village: { padR: 85, plazaR: 11, spokes: 3, density: 0.6, label: "VILLAGE" },
  town: { padR: 160, plazaR: 15, spokes: 4, density: 0.7, label: "TOWN" },
  // Cities are MASSIVE — the base padR is re-drawn per city (460–640 m) in
  // buildTowns, and the layout comes from the dedicated city generator.
  city: { padR: 550, plazaR: 32, spokes: 6, density: 0.8, label: "CITY" },
};

const STREET_W = 5;
/** Candidate directions scanned per planet (Fibonacci sphere). */
const CANDIDATES = 1024;
/** Candidates that graduate to full terrain probing. */
const FULL_EVALS = 220;
/** Extra pad-to-pad separation beyond the apron radii (m). */
const MIN_SEPARATION_M = 900;
/** Reject a site when the pad ring's terrain relief exceeds this budget. */
const RELIEF_BUDGET_M = (padR: number): number => 9 + padR * 0.05;

function smooth01(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

/** Deterministic tangent basis at a unit direction (rivers.ts convention). */
export function townBasis(
  d: readonly [number, number, number] | { x: number; y: number; z: number },
): [[number, number, number], [number, number, number]] {
  const dx = Array.isArray(d) ? d[0] : (d as { x: number }).x;
  const dy = Array.isArray(d) ? d[1] : (d as { y: number }).y;
  const dz = Array.isArray(d) ? d[2] : (d as { z: number }).z;
  const ax = Math.abs(dx) < 0.9 ? 1 : 0;
  const ay = 1 - ax;
  let t1x = ay * dz,
    t1y = -ax * dz,
    t1z = ax * dy - ay * dx;
  const l1 = pmath.hypot(t1x, t1y, t1z) || 1;
  t1x /= l1;
  t1y /= l1;
  t1z /= l1;
  const t2x = dy * t1z - dz * t1y;
  const t2y = dz * t1x - dx * t1z;
  const t2z = dx * t1y - dy * t1x;
  return [
    [t1x, t1y, t1z],
    [t2x, t2y, t2z],
  ];
}

// ---------------------------------------------------------------------------
// Town field — the registry + the sample()-hot-path pad query
// ---------------------------------------------------------------------------

export class TownField {
  constructor(
    readonly towns: TownDef[],
    private readonly R: number,
  ) {}

  /**
   * Pad influence at a planet-local unit direction. At most one town can hit
   * (separation is enforced at build time), so this returns on first match.
   * Cheap-reject is one dot product per town.
   */
  padAt(dx: number, dy: number, dz: number): TownPad | null {
    for (const t of this.towns) {
      const dot = dx * t.dir[0] + dy * t.dir[1] + dz * t.dir[2];
      if (dot <= t.cosApron) continue;
      const dist = Math.sqrt(Math.max(0, 2 - 2 * dot)) * this.R; // chord ≈ arc
      if (dist >= t.apronR) continue;
      // Bearing in the tangent frame → lobed edge at this angle. Only paid
      // within the (generous) apron cone, never on the open wild.
      const pu = dx * t.t1[0] + dy * t.t1[1] + dz * t.t1[2];
      const pv = dx * t.t2[0] + dy * t.t2[1] + dz * t.t2[2];
      const th = pmath.atan2(pv, pu);
      const coreR = townRadiusAt(t, th);
      const farmR = coreR + t.farmW;
      const apron = farmR + t.blendW;
      if (dist >= apron) continue;
      const k = 1 - smooth01((dist - farmR) / t.blendW);
      return { k, h: t.padH + dist * dist * t.sag, town: t, dist, coreR, farmR };
    }
    return null;
  }

  /** Nearest town whose (apron + slack) covers the direction, else null. */
  nearest(dx: number, dy: number, dz: number, slackM = 0): { town: TownDef; dist: number } | null {
    let best: { town: TownDef; dist: number } | null = null;
    for (const t of this.towns) {
      const dot = dx * t.dir[0] + dy * t.dir[1] + dz * t.dir[2];
      const dist = Math.sqrt(Math.max(0, 2 - 2 * dot)) * this.R;
      if (dist < t.apronR + slackM && (best === null || dist < best.dist)) {
        best = { town: t, dist };
      }
    }
    return best;
  }
}

export const EMPTY_TOWNS = new TownField([], 1);

/**
 * Process-wide town registry cache, keyed by planet seed. The single source
 * every consumer resolves through — `PlanetSampler.towns()` and the resource
 * placement in resources.ts both land here, so one build per planet per JS
 * context and NO way for two code paths to disagree. Pass a sampler when you
 * already have one; otherwise a throwaway is built on cache miss.
 */
const FIELDS = new Map<string, TownField>();

export function townFieldFor(def: PlanetDef, sampler?: PlanetSampler): TownField {
  let f = FIELDS.get(def.seed);
  if (!f) {
    f = buildTowns(sampler ?? new PlanetSampler(def));
    FIELDS.set(def.seed, f);
    if (FIELDS.size > 96) {
      const first = FIELDS.keys().next().value;
      if (first !== undefined) FIELDS.delete(first);
    }
  }
  return f;
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Registry build — pure function of the planet seed
// ---------------------------------------------------------------------------

/**
 * The tier wish-list for a planet, largest first. Drawn with FIXED-ORDER rng
 * draws (resources.ts convention) so the stream stays aligned forever.
 */
function tierPlan(def: PlanetDef, rng: Rng): TownTier[] {
  const wishes: TownTier[] = [];
  const moon = !!def.parentId;
  const r1 = rng();
  const r2 = rng();
  const r3 = rng();
  if (moon) {
    // Moons at most host a single outpost.
    if (r1 < 0.35) wishes.push("outpost");
    return wishes;
  }
  switch (def.archetype) {
    case "terran":
      if (r1 < 0.6) wishes.push("city");
      wishes.push("town");
      if (r2 < 0.6) wishes.push("town");
      wishes.push("village", "village", "hamlet", "hamlet");
      if (r3 < 0.7) wishes.push("outpost");
      break;
    case "ocean":
      if (r1 < 0.5) wishes.push("town");
      wishes.push("village");
      if (r2 < 0.7) wishes.push("village");
      wishes.push("hamlet", "hamlet");
      break;
    case "desert":
      if (r1 < 0.35) wishes.push("town");
      wishes.push("village", "hamlet");
      if (r2 < 0.7) wishes.push("hamlet");
      wishes.push("outpost", "outpost");
      break;
    case "ice":
      wishes.push("village");
      if (r1 < 0.6) wishes.push("hamlet");
      wishes.push("hamlet", "outpost", "outpost");
      break;
    case "volcanic":
      if (r1 < 0.7) wishes.push("outpost");
      if (r2 < 0.4) wishes.push("outpost");
      break;
    case "barren":
      if (r1 < 0.5) wishes.push("outpost");
      break;
  }
  return wishes;
}

/**
 * Build the settlement registry for a planet. Called lazily (and cached) by
 * `PlanetSampler.towns()` — one-time cost per planet, same class as the river
 * graph build. All terrain probing passes `skipTowns` so siting sees the
 * PRE-town terrain (that pre-town level is what the pad freezes as `padH`).
 */
export function buildTowns(sampler: PlanetSampler): TownField {
  const def = sampler.def;
  const R = def.radius;
  const amp = sampler.amplitude;
  const rng = rngFromString(`${def.seed}:towns`);
  const wishes = tierPlan(def, rng);
  if (wishes.length === 0) return new TownField([], R);

  // --- Candidate pass: Fibonacci sphere, scored on the cheap macro field ---
  const phase = rng() * Math.PI * 2;
  const ga = Math.PI * (3 - Math.sqrt(5));
  const seaRel = sampler.seaRel;
  const hasSea = def.seaRadius !== null;
  const cands: { x: number; y: number; z: number; score: number }[] = [];
  for (let i = 0; i < CANDIDATES; i++) {
    const yv = 1 - (2 * (i + 0.5)) / CANDIDATES;
    const rr = Math.sqrt(Math.max(0, 1 - yv * yv));
    const a = phase + ga * i;
    const x = pmath.cos(a) * rr;
    const z = pmath.sin(a) * rr;
    const hC = sampler.continentalH(x, yv, z);
    if (hasSea && hC < seaRel + 0.03) continue; // underwater / tidal
    if (hC > 0.45) continue; // highlands — settlements keep to open country
    // Prefer low coastal plains; deterministic tie-break by index.
    const coastal = hasSea ? 1 - Math.min(1, Math.abs(hC - (seaRel + 0.08)) / 0.3) : 0.5;
    cands.push({ x, y: yv, z, score: coastal - i * 1e-6 });
  }
  cands.sort((a, b) => b.score - a.score);
  if (cands.length > FULL_EVALS) cands.length = FULL_EVALS;

  // --- Greedy selection: largest tiers first, first candidate that fits ---
  const towns: TownDef[] = [];
  const center: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const probe: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const used = new Set<number>();

  for (const tier of wishes) {
    const spec = TOWN_TIERS[tier];
    // Per-city size draw (FIXED-ORDER rng): cities vary massively, 460–640 m.
    const wishR = tier === "city" ? Math.round(range(rng, 460, 640)) : spec.padR;
    // --- Boundary identity (drawn per WISH so the stream stays aligned) ---
    // Lobed edge: amp/k^1.2 harmonics; strong for big settlements.
    const A = tier === "city" || tier === "town" ? range(rng, 0.2, 0.32) : range(rng, 0.1, 0.2);
    const warpAmp: number[] = [];
    const warpPh: number[] = [];
    for (let k = 2; k <= 6; k++) {
      warpAmp.push(A / pmath.pow(k, 1.2));
      warpPh.push(rng() * Math.PI * 2);
    }
    // Exit roads: a through-route pair plus extras, ≥~30° apart. These
    // azimuths triple-duty as pad FINGERS, road directions, and wall gates.
    const exitN =
      tier === "city"
        ? 3 + Math.floor(rng() * 3)
        : tier === "town"
          ? 2 + Math.floor(rng() * 3)
          : tier === "outpost"
            ? 1
            : 1 + Math.floor(rng() * 2);
    const fingers: { a: number; f: number; s: number }[] = [];
    const a0 = rng() * Math.PI * 2;
    for (let e = 0; e < exitN; e++) {
      // First two form the through-route (roughly opposite).
      const a =
        e === 0
          ? a0
          : e === 1
            ? a0 + Math.PI + range(rng, -0.44, 0.44)
            : a0 + ((e - 1) / (exitN - 1)) * Math.PI * 2 + range(rng, -0.35, 0.35);
      fingers.push({ a, f: range(rng, 0.18, 0.4), s: range(rng, 0.14, 0.26) });
    }
    const farmW =
      tier === "city"
        ? Math.round(range(rng, 170, 280))
        : tier === "town"
          ? Math.round(range(rng, 110, 190))
          : tier === "village"
            ? Math.round(range(rng, 80, 140))
            : tier === "hamlet"
              ? Math.round(range(rng, 50, 90))
              : 0;
    // A massive pad that finds no flat-enough land retries at 3/4 scale
    // (no extra draws — the stream stays aligned).
    const attempts = tier === "city" ? [wishR, Math.round(wishR * 0.75)] : [wishR];
    let placed = false;

    for (const padR of attempts) {
    if (placed) break;
    const blendW = 30 + Math.min(padR * 0.5, 300);
    // Worst-case outer edge (containment/separation/cutoff budgets).
    let maxW = 1;
    for (const a of warpAmp) maxW += a;
    for (const f of fingers) maxW += f.f;
    const apronR = padR * maxW + farmW + blendW;

    for (let ci = 0; ci < cands.length && !placed; ci++) {
      if (used.has(ci)) continue;
      const cd = cands[ci];

      // Separation from already-placed towns.
      let clear = true;
      for (const t of towns) {
        const dot = cd.x * t.dir[0] + cd.y * t.dir[1] + cd.z * t.dir[2];
        const dist = Math.sqrt(Math.max(0, 2 - 2 * dot)) * R;
        if (dist < t.apronR + apronR + MIN_SEPARATION_M) {
          clear = false;
          break;
        }
      }
      if (!clear) continue;

      // Center probe (climate for the biome record).
      sampler.sample(cd.x, cd.y, cd.z, center, true, false, true);
      if (center.river > 0.02) continue;
      if (center.mountain > 0.55) continue;
      if (hasSea && center.h < seaRel + 4 / amp) continue;
      if (center.temp < (def.archetype === "ice" ? -0.9 : -0.75)) continue;

      // Ring probes: relief budget + no water anywhere on the pad.
      // 8 on the pad rim (full circle), 4 mid-pad (rivers crossing the
      // interior), 4 on the apron (full circle, offset) — each set spans
      // the whole circle so no arc goes unchecked.
      const [t1, t2] = townBasis([cd.x, cd.y, cd.z]);
      let minH = center.h;
      let maxH = center.h;
      let wet = false;
      for (let k = 0; k < 16 && !wet; k++) {
        const mid = k >= 8 && k < 12;
        const apron = k >= 12;
        const ring = apron ? apronR : mid ? padR * 0.55 : padR;
        const a = apron
          ? ((k - 12) / 4) * Math.PI * 2 + 0.39 + Math.PI / 8
          : mid
            ? ((k - 8) / 4) * Math.PI * 2 + 0.39 + Math.PI / 4
            : (k / 8) * Math.PI * 2 + 0.39;
        const ox = (pmath.cos(a) * ring) / R;
        const oz = (pmath.sin(a) * ring) / R;
        let px = cd.x + t1[0] * ox + t2[0] * oz;
        let py = cd.y + t1[1] * ox + t2[1] * oz;
        let pz = cd.z + t1[2] * ox + t2[2] * oz;
        const l = pmath.hypot(px, py, pz) || 1;
        px /= l;
        py /= l;
        pz /= l;
        sampler.sample(px, py, pz, probe, false, false, true);
        if (probe.river > (apron ? 0.3 : 0.05)) wet = true;
        if (hasSea && probe.h < seaRel + 2 / amp) wet = true;
        if (!apron) {
          if (probe.h < minH) minH = probe.h;
          if (probe.h > maxH) maxH = probe.h;
        }
      }
      if (wet) continue;
      if ((maxH - minH) * amp > RELIEF_BUDGET_M(padR)) continue;

      // Accept. Pad level = mean of the core probes, kept above the sea.
      let padH = (minH + maxH) / 2;
      if (hasSea) padH = Math.max(padH, seaRel + 3 / amp);
      const id = towns.length;
      const seed = `${def.seed}:town:${id}`;
      const padBaseR = R + padH * amp;
      const ar = apronR / R;
      towns.push({
        id,
        seed,
        name: settlementName(seed),
        dir: [cd.x, cd.y, cd.z],
        tier,
        padR,
        apronR,
        padH,
        // Tangent-plane sag: the pad follows the PLANE, not the sphere, so
        // the rigid town meshes sit exactly on the ground everywhere.
        sag: 1 / (2 * padBaseR * amp),
        biome: { temp: center.temp, moist: center.moist },
        // cos of the apron angle (small-angle safe form): 1 - chord²/2.
        cosApron: 1 - (ar * ar) / 2,
        t1,
        t2,
        warpAmp,
        warpPh,
        fingers,
        farmW,
        blendW,
        });
      used.add(ci);
      placed = true;
    }
    }
  }

  return new TownField(towns, R);
}

// ---------------------------------------------------------------------------
// Layout — streets and building footprints, lazily built and cached
// ---------------------------------------------------------------------------

const LAYOUTS = new Map<string, TownLayout>();

/** Corners of a footprint rect in town-local space. */
function rectCorners(b: BuildingDef): [number, number][] {
  const cy = pmath.cos(b.yaw);
  const sy = pmath.sin(b.yaw);
  const hw = b.w / 2;
  const hd = b.d / 2;
  const out: [number, number][] = [];
  for (const [su, sv] of [[1, 1], [1, -1], [-1, -1], [-1, 1]] as const) {
    out.push([b.u + su * hw * cy - sv * hd * sy, b.v + su * hw * sy + sv * hd * cy]);
  }
  return out;
}

/** SAT overlap test for two yaw-rotated footprints (with a clearance gap). */
function rectsOverlap(a: BuildingDef, b: BuildingDef, gap: number): boolean {
  // Euclidean quick-reject on summed half-diagonals (sound for any yaw —
  // Manhattan under-reaches for rotated elongated rects). Build-time only.
  const quick = (pmath.hypot(a.w, a.d) + pmath.hypot(b.w, b.d)) / 2 + gap;
  if (pmath.hypot(a.u - b.u, a.v - b.v) > quick) return false;
  const ca = rectCorners(a);
  const cb = rectCorners(b);
  for (const r of [a, b]) {
    for (const ang of [r.yaw, r.yaw + Math.PI / 2]) {
      const ax = pmath.cos(ang);
      const ay = pmath.sin(ang);
      let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
      for (const [u, v] of ca) {
        const p = u * ax + v * ay;
        if (p < minA) minA = p;
        if (p > maxA) maxA = p;
      }
      for (const [u, v] of cb) {
        const p = u * ax + v * ay;
        if (p < minB) minB = p;
        if (p > maxB) maxB = p;
      }
      if (maxA + gap < minB || maxB + gap < minA) return false; // separating axis
    }
  }
  return true;
}

/** Spatial-hash cell size (m) — footprint queries touch one cell. */
const CELL_SIZE = 26;
const CELL_STRIDE = 16384;

/** Index every footprint into the cells its (grown) AABB overlaps. */
function buildCells(buildings: BuildingDef[]): Map<number, number[]> {
  const cells = new Map<number, number[]>();
  for (let i = 0; i < buildings.length; i++) {
    const b = buildings[i];
    // Conservative: half-diagonal + the largest EFFECTIVE query reach —
    // the Euclidean pre-filter reaches √((w/2+c+1)² + (d/2+c+1)²) ≤
    // half-diag + (c+1)·√2 ≈ half-diag + 2.6 for the server's capR 0.8.
    const half = pmath.hypot(b.w, b.d) / 2 + 3;
    const ix0 = Math.floor((b.u - half) / CELL_SIZE);
    const ix1 = Math.floor((b.u + half) / CELL_SIZE);
    const iz0 = Math.floor((b.v - half) / CELL_SIZE);
    const iz1 = Math.floor((b.v + half) / CELL_SIZE);
    for (let ix = ix0; ix <= ix1; ix++) {
      for (let iz = iz0; iz <= iz1; iz++) {
        const key = ix * CELL_STRIDE + iz;
        let list = cells.get(key);
        if (!list) cells.set(key, (list = []));
        list.push(i);
      }
    }
  }
  return cells;
}

/**
 * A ROW of houses along a line — the burgage fabric at any scale: party-wall
 * runs (3–6 houses abutting) separated by wide breaks, ragged depths, an
 * occasional two-rect composite (an addition), a rare skewed elder that
 * predates the lane. Returns the number of houses placed.
 */
function emitHouseRow(
  rng: Rng,
  push: (b: BuildingDef) => void,
  blocked: (b: BuildingDef) => boolean,
  o: {
    x0: number; // row start (town-local)
    z0: number;
    dir: number; // row direction (radians)
    length: number;
    face: number; // door side: +1 = left of dir, -1 = right
    density: number;
    floors: number; // storey band (jittered per run)
    tint: number;
    steep: number; // roof pitch scale
    depthBase: number;
  },
): number {
  const cy = pmath.cos(o.dir);
  const sy = pmath.sin(o.dir);
  let x = 0;
  let placed = 0;
  let run = 2 + Math.floor(rng() * 5);
  let runFloors = Math.max(1, o.floors + [-1, 0, 0, 1][Math.floor(rng() * 4)]);
  while (x + 4 < o.length) {
    const lotW = range(rng, 4.8, 8.5);
    const gap = run > 0 ? range(rng, 0.15, 0.6) : range(rng, 4, 11);
    if (run <= 0) {
      run = 2 + Math.floor(rng() * 5);
      runFloors = Math.max(1, o.floors + [-1, 0, 0, 1][Math.floor(rng() * 4)]);
    }
    run--;
    const w = Math.min(lotW, o.length - x);
    const rare = rng();
    const occupied = rng() < o.density;
    const depth = o.depthBase + range(rng, -1.3, 1.3);
    const seed = Math.floor(rng() * 1e9);
    const skewed = rare < 1 / 18;
    const skew = skewed ? range(rng, 0.3, 0.55) * (rng() < 0.5 ? 1 : -1) : 0;
    const cx = x + w / 2 + (skewed ? 1.6 : 0);
    x += w + gap + (skewed ? 3 : 0);
    if (w < 3.8 || !occupied) continue;
    const back = o.face * (depth / 2 + (skewed ? 2 : 0));
    const u = o.x0 + cx * cy - back * sy;
    const v = o.z0 + cx * sy + back * cy;
    const mk = (uu: number, vv: number, ww: number, ff: number, sd: number): BuildingDef => ({
      u: uu,
      v: vv,
      yaw: o.dir + skew,
      w: ww,
      d: depth * (skewed ? 0.8 : 1),
      h: ff * 2.95 + range(rng, 0.4, 0.8),
      roofH: o.steep * Math.min(1, ww / 6.5),
      kind: "dwelling",
      roof: "pitched",
      tint: o.tint,
      seed: sd,
    });
    if (!skewed && w > 7 && rng() < 0.15) {
      const w1 = w * range(rng, 0.38, 0.55);
      const f2 = Math.max(1, runFloors + (rng() < 0.5 ? -1 : 1));
      const b1 = mk(u - ((w - w1) / 2) * cy, v - ((w - w1) / 2) * sy, w1, runFloors, seed);
      const b2 = mk(u + (w1 / 2 - 0.01) * cy, v + (w1 / 2 - 0.01) * sy, w - w1, f2, Math.floor(rng() * 1e9));
      if (!blocked(b1) && !blocked(b2)) {
        push(b1);
        push(b2);
        placed += 2;
      }
      continue;
    }
    const b = mk(u, v, w, runFloors, seed);
    if (!blocked(b)) {
      push(b);
      placed++;
    }
  }
  return placed;
}

/**
 * The WORKING LANDSCAPE belt — strip-field furlongs, orchards on strict
 * grids, farmsteads along the exit roads, a walled graveyard, a windmill.
 * The classic settlement signal from the air: a town ringed by fields
 * answers "why is this here" in a way no street pattern can.
 */
function emitFarmBelt(
  town: TownDef,
  rng: Rng,
  patches: GroundPatch[],
  props: TownProp[],
  addBuilding: (b: BuildingDef) => void,
): void {
  if (town.farmW < 40) return;
  const nearFinger = (th: number, half: number): boolean => {
    for (const f of town.fingers) {
      let da = th - f.a;
      da -= Math.round(da / (Math.PI * 2)) * (Math.PI * 2);
      if (Math.abs(da) < half) return true;
    }
    return false;
  };

  // --- Furlongs: bundles of parallel selion strips, orientation alternating
  // per furlong (adjacent bundles disagree — correlated, not i.i.d.). ---
  let th = rng() * Math.PI * 2;
  const thEnd = th + Math.PI * 2;
  while (th < thEnd) {
    const coreR = townRadiusAt(town, th);
    const rIn = coreR + 8;
    const rOut = coreR + town.farmW - 8;
    const rMid = (rIn + rOut) / 2;
    const arc = range(rng, 110, 190);
    const dTh = arc / rMid;
    const radial = rng() < 0.55;
    const baseTint = Math.floor(rng() * 3);
    const depth = rOut - rIn - 4;
    if (depth > 26) {
      if (radial) {
        const sw = range(rng, 6.5, 11);
        const n = Math.max(1, Math.floor(arc / sw));
        for (let i = 0; i < n; i++) {
          const a = th + ((i + 0.5) / n) * dTh;
          const roll = rng();
          if (nearFinger(a, 8 / rMid)) continue; // fields part around roads
          patches.push({
            u: pmath.cos(a) * rMid,
            v: pmath.sin(a) * rMid,
            yaw: a,
            w: depth,
            d: sw - 1,
            tint: roll < 0.22 ? 3 : (baseTint + (roll > 0.75 ? 1 : 0)) % 3,
          });
        }
      } else {
        const sw = range(rng, 6.5, 11);
        const n = Math.max(1, Math.floor(depth / sw));
        const aMid = th + dTh / 2;
        if (!nearFinger(aMid, (arc * 0.5 + 8) / rMid)) {
          for (let i = 0; i < n; i++) {
            const rr = rIn + (i + 0.5) * (depth / n);
            const roll = rng();
            patches.push({
              u: pmath.cos(aMid) * rr,
              v: pmath.sin(aMid) * rr,
              yaw: aMid + Math.PI / 2,
              w: arc - 6,
              d: depth / n - 1,
              tint: roll < 0.22 ? 3 : (baseTint + (roll > 0.75 ? 1 : 0)) % 3,
            });
          }
        }
      }
    }
    th += dTh * range(rng, 1.0, 1.35);
  }

  // --- Farmsteads along the exit roads (the spill that feathers the edge) ---
  for (const f of town.fingers.slice(0, 4)) {
    if (rng() > 0.7) continue;
    const coreR = townRadiusAt(town, f.a);
    const rr = coreR + range(rng, 0.25, 0.7) * town.farmW;
    const side = rng() < 0.5 ? 1 : -1;
    const off = side * range(rng, 14, 28);
    const yaw = f.a + range(rng, -0.15, 0.15);
    const cu = pmath.cos(f.a) * rr - pmath.sin(f.a) * off;
    const cv = pmath.sin(f.a) * rr + pmath.cos(f.a) * off;
    addBuilding({
      u: cu, v: cv, yaw,
      w: range(rng, 5, 7), d: range(rng, 5, 6), h: range(rng, 3.2, 3.8),
      roofH: range(rng, 1.4, 2), kind: "dwelling", roof: "pitched", tint: 0,
      seed: Math.floor(rng() * 1e9),
    });
    addBuilding({
      u: cu + pmath.cos(yaw + Math.PI / 2) * range(rng, 10, 14),
      v: cv + pmath.sin(yaw + Math.PI / 2) * range(rng, 10, 14),
      yaw: yaw + range(rng, -0.2, 0.2),
      w: range(rng, 8, 12), d: range(rng, 6, 7), h: range(rng, 4, 5),
      roofH: range(rng, 1.8, 2.4), kind: "barn", roof: "pitched", tint: 1,
      seed: Math.floor(rng() * 1e9),
    });
  }

  // --- Orchards: trees on a STRICT grid (regular grids read as cultivation) ---
  const orchards = 1 + Math.floor(rng() * 2);
  for (let o = 0; o < orchards; o++) {
    const a = rng() * Math.PI * 2;
    if (nearFinger(a, 0.25)) continue;
    const coreR = townRadiusAt(town, a);
    const w = range(rng, 40, 68);
    const d = range(rng, 28, 48);
    const rr = coreR + 12 + d / 2 + rng() * Math.max(4, town.farmW - d - 30);
    const cu = pmath.cos(a) * rr;
    const cv = pmath.sin(a) * rr;
    patches.push({ u: cu, v: cv, yaw: a, w, d, tint: 4 });
    const step = range(rng, 6.5, 8);
    for (let x = -w / 2 + 3; x < w / 2 - 2; x += step) {
      for (let z = -d / 2 + 3; z < d / 2 - 2; z += step) {
        props.push({
          kind: "tree",
          u: cu + pmath.cos(a) * x - pmath.sin(a) * z,
          v: cv + pmath.sin(a) * x + pmath.cos(a) * z,
          s: range(rng, 0.85, 1.15),
          yaw: rng() * Math.PI * 2,
        });
      }
    }
  }

  // --- The walled graveyard at the edge (absurd believability-per-cost) ---
  if (rng() < 0.7) {
    const a = rng() * Math.PI * 2;
    const coreR = townRadiusAt(town, a);
    const rr = coreR + 14;
    const cu = pmath.cos(a) * rr;
    const cv = pmath.sin(a) * rr;
    const gw = 24;
    const gd = 17;
    patches.push({ u: cu, v: cv, yaw: a, w: gw, d: gd, tint: 5 });
    for (const [ox, oz, ww, dd] of [
      [0, -gd / 2, gw, 0.5],
      [0, gd / 2, gw, 0.5],
      [-gw / 2, 0, 0.5, gd],
      [gw / 2, 0, 0.5, gd],
    ] as const) {
      addBuilding({
        u: cu + pmath.cos(a) * ox - pmath.sin(a) * oz,
        v: cv + pmath.sin(a) * ox + pmath.cos(a) * oz,
        yaw: a, w: ww, d: dd, h: 1.1, roofH: 0, kind: "wall", roof: "flat",
        tint: 2, seed: Math.floor(rng() * 1e9),
      });
    }
    for (let gx = -gw / 2 + 3; gx < gw / 2 - 2; gx += 3.2) {
      for (let gz = -gd / 2 + 3; gz < gd / 2 - 2; gz += 3.8) {
        if (rng() < 0.25) continue;
        props.push({
          kind: "slab",
          u: cu + pmath.cos(a) * (gx + range(rng, -0.4, 0.4)) - pmath.sin(a) * gz,
          v: cv + pmath.sin(a) * (gx + range(rng, -0.4, 0.4)) + pmath.cos(a) * gz,
          s: range(rng, 0.8, 1.15),
          yaw: a + range(rng, -0.12, 0.12),
        });
      }
    }
    props.push({ kind: "tree", u: cu + pmath.cos(a) * (gw / 2 + 4), v: cv + pmath.sin(a) * (gw / 2 + 4), s: 1.4, yaw: 0 });
  }

  // --- One windmill on the outer belt ---
  if (rng() < 0.6 && town.fingers.length > 0) {
    const f = town.fingers[Math.floor(rng() * town.fingers.length)];
    const a = f.a + range(rng, 0.3, 0.55) * (rng() < 0.5 ? 1 : -1);
    const coreR = townRadiusAt(town, a);
    const rr = coreR + town.farmW * range(rng, 0.7, 0.9);
    addBuilding({
      u: pmath.cos(a) * rr, v: pmath.sin(a) * rr, yaw: rng() * Math.PI * 2,
      w: 4, d: 4, h: range(rng, 10, 12.5), roofH: 1.6, kind: "windmill",
      roof: "pitched", tint: 1, seed: Math.floor(rng() * 1e9),
    });
  }
}

/** The street/lot plan for a settlement — deterministic, cached by seed. */
export function townLayout(town: TownDef): TownLayout {
  let cached = LAYOUTS.get(town.seed);
  if (cached) return cached;
  const rng = rngFromString(`${town.seed}:layout`);
  cached = town.tier === "city" ? cityLayout(town, rng) : smallLayout(town, rng);
  cached.cells = buildCells(cached.buildings);
  // Lamps that landed inside a footprint (deep rows along narrow blocks,
  // the twin seam) are culled here with the fresh spatial hash — no rng,
  // one-time, covers every plan mechanism.
  cached.lamps = cached.lamps.filter((l) => {
    const idx = cached!.cells.get(
      Math.floor(l.u / cached!.cellSize) * CELL_STRIDE + Math.floor(l.v / cached!.cellSize),
    );
    if (!idx) return true;
    for (const bi of idx) {
      const b = cached!.buildings[bi];
      const du = l.u - b.u;
      const dv = l.v - b.v;
      const cy = pmath.cos(b.yaw);
      const sy = pmath.sin(b.yaw);
      const qx = du * cy + dv * sy;
      const qz = -du * sy + dv * cy;
      if (Math.abs(qx) < b.w / 2 + 0.6 && Math.abs(qz) < b.d / 2 + 0.6) return false;
    }
    return true;
  });
  // C3 quest board: one kiosk at the plaza rim for village+ settlements, from
  // a DEDICATED rng stream (see townBoardPos) — consumes NO layout draw, so
  // the town/npc determinism suites stay bit-identical.
  const bp = townBoardPos(town, cached);
  if (bp) {
    cached.props.push({
      kind: "board",
      u: bp.u,
      v: bp.v,
      s: 1,
      yaw: pmath.atan2(cached.plazaV - bp.v, cached.plazaU - bp.u),
    });
  }
  LAYOUTS.set(town.seed, cached);
  if (LAYOUTS.size > 96) {
    const first = LAYOUTS.keys().next().value;
    if (first !== undefined) LAYOUTS.delete(first);
  }
  return cached;
}

/**
 * The quest board kiosk position for a settlement (town-local meters), or
 * null when the settlement has no board (outpost/hamlet) or every candidate
 * angle is blocked by a building footprint. Deterministic from a DEDICATED
 * rng stream (`${town.seed}:board`) — it consumes none of the layout's draws,
 * so `test:towns`/`test:npcs` determinism stays bit-identical. Tries ~8 angles
 * around the plaza rim (~0.72·plazaR out) and takes the first clear one.
 */
export function townBoardPos(town: TownDef, layout: TownLayout): { u: number; v: number } | null {
  if (town.tier === "outpost" || town.tier === "hamlet") return null;
  const rng = rngFromString(`${town.seed}:board`);
  const a0 = rng() * Math.PI * 2;
  const off = layout.plazaR * 0.72;
  const N = 8;
  for (let k = 0; k < N; k++) {
    const a = a0 + (k / N) * Math.PI * 2;
    const u = layout.plazaU + pmath.cos(a) * off;
    const v = layout.plazaV + pmath.sin(a) * off;
    if (!boardBlocked(layout, u, v, 1.0)) return { u, v };
  }
  return null;
}

/** True when (u, v) lands within `clear` m of any building footprint. */
function boardBlocked(layout: TownLayout, u: number, v: number, clear: number): boolean {
  const cell =
    layout.cells && layout.cells.size > 0
      ? layout.cells.get(Math.floor(u / layout.cellSize) * CELL_STRIDE + Math.floor(v / layout.cellSize))
      : null;
  if (cell) {
    for (const bi of cell) if (footHit(layout.buildings[bi], u, v, clear)) return true;
    return false;
  }
  // No spatial hash (not yet built) — full scan.
  if (layout.cells && layout.cells.size > 0) return false;
  for (const b of layout.buildings) if (footHit(b, u, v, clear)) return true;
  return false;
}

function footHit(b: BuildingDef, u: number, v: number, clear: number): boolean {
  const du = u - b.u;
  const dv = v - b.v;
  const cy = pmath.cos(b.yaw);
  const sy = pmath.sin(b.yaw);
  const qx = du * cy + dv * sy;
  const qz = -du * sy + dv * cy;
  return Math.abs(qx) < b.w / 2 + clear && Math.abs(qz) < b.d / 2 + clear;
}

/**
 * Small settlements (outpost → town) — organic archetypes, not rings:
 *  - row        houses strung along the through-road (the street village)
 *  - green      houses ringing an irregular common (the green village)
 *  - ring       bent concentric rows (the nucleated village)
 *  - crossroads clusters at a junction (the waypoint hamlet)
 *  - cluster    a farmstead knot around a yard (outposts, lone hamlets)
 * All share the lobed boundary, exit roads, farm belt and burgage rows the
 * cities use — the same organic language at a smaller scale.
 */
function smallLayout(town: TownDef, rng: Rng): TownLayout {
  const spec = TOWN_TIERS[town.tier];
  const plazaR = spec.plazaR;
  const buildings: BuildingDef[] = [];
  const patches: GroundPatch[] = [];
  const props: TownProp[] = [];
  const streets: StreetSeg[] = [];
  const parks: { u: number; v: number; r: number }[] = [];
  const lamps: { u: number; v: number }[] = [];
  let maxH = 0;
  const landmarks: BuildingDef[] = [];
  const push = (b: BuildingDef): void => {
    buildings.push(b);
    const top = b.h + b.roofH;
    if (top > maxH) maxH = top;
  };
  const blocked = (b: BuildingDef): boolean => {
    for (const l of landmarks) if (rectsOverlap(b, l, 1.5)) return true;
    // Belt-and-braces vs ALL earlier placements: near-parallel road rows
    // cross near the center at shallow angles — first-placed wins. The
    // NEGATIVE gap still allows party-wall abutment (≤0.28 m).
    for (const other of buildings) if (rectsOverlap(b, other, -0.28)) return true;
    return false;
  };

  const archetype =
    town.tier === "outpost"
      ? "cluster"
      : town.tier === "hamlet"
        ? pick(rng, ["row", "cluster", "crossroads"] as const)
        : town.tier === "village"
          ? pick(rng, ["row", "green", "ring"] as const)
          : pick(rng, ["green", "ring", "row"] as const); // town

  // Through-route: the first two exit roads (or an axis if only one).
  const roadA = town.fingers[0]?.a ?? rng() * Math.PI * 2;
  const roadB = town.fingers[1]?.a ?? roadA + Math.PI + range(rng, -0.3, 0.3);
  const edgeAt = (a: number): number => townRadiusAt(town, a);

  // Streets: chunk-clipped to the lobed edge; exits run to the farm fringe.
  const pushClipped = (u0: number, v0: number, u1: number, v1: number, w: number): void => {
    const len = pmath.hypot(u1 - u0, v1 - v0);
    if (len < 5) return;
    const n = Math.max(1, Math.ceil(len / 22));
    for (let i = 0; i < n; i++) {
      const q0 = i / n;
      const q1 = (i + 1) / n;
      const mu = u0 + (u1 - u0) * ((q0 + q1) / 2);
      const mv = v0 + (v1 - v0) * ((q0 + q1) / 2);
      if (pmath.hypot(mu, mv) > townRadiusAt(town, pmath.atan2(mv, mu)) - 2) continue;
      streets.push({
        u0: u0 + (u1 - u0) * q0,
        v0: v0 + (v1 - v0) * q0,
        u1: u0 + (u1 - u0) * q1,
        v1: v0 + (v1 - v0) * q1,
        w,
      });
    }
  };
  for (const f of town.fingers) {
    const end = edgeAt(f.a) + town.farmW * 0.92;
    streets.push({
      u0: 0,
      v0: 0,
      u1: pmath.cos(f.a) * end,
      v1: pmath.sin(f.a) * end,
      w: 5,
    });
  }

  // --- The weenie: even a village has a silhouette (chapel + spirelet) ---
  if (town.tier === "village" || town.tier === "town") {
    const big = town.tier === "town";
    const la = roadA + Math.PI / 2 + range(rng, -0.4, 0.4);
    const lr = archetype === "green" ? plazaR * range(rng, 1.8, 2.3) + 6 : range(rng, 10, 18);
    const cu = pmath.cos(la) * lr;
    const cv = pmath.sin(la) * lr;
    const yaw = la + range(rng, -0.3, 0.3);
    const nave: BuildingDef = {
      u: cu,
      v: cv,
      yaw,
      w: big ? range(rng, 7, 9) : range(rng, 5.5, 6.5),
      d: big ? range(rng, 11, 14) : range(rng, 8, 10),
      h: big ? range(rng, 6, 7) : range(rng, 4.5, 5.5),
      roofH: big ? range(rng, 3, 4) : range(rng, 2.4, 3.2),
      kind: "hall",
      roof: "pitched",
      tint: 2,
      seed: Math.floor(rng() * 1e9),
    };
    landmarks.push(nave);
    push(nave);
    const sw = big ? 3.4 : 2.8;
    const spire: BuildingDef = {
      u: cu + pmath.cos(yaw) * (nave.w / 2 + sw / 2 - 0.4),
      v: cv + pmath.sin(yaw) * (nave.w / 2 + sw / 2 - 0.4),
      yaw,
      w: sw,
      d: sw,
      h: big ? range(rng, 13, 17) : range(rng, 9, 13),
      roofH: big ? range(rng, 3.5, 5) : range(rng, 2.5, 3.5),
      kind: "spire",
      roof: "pitched",
      tint: 2,
      seed: Math.floor(rng() * 1e9),
    };
    landmarks.push(spire);
    push(spire);
  }
  if (town.tier === "outpost") {
    // A comms mast — the lonely landmark of a frontier post.
    const la = rng() * Math.PI * 2;
    const mast: BuildingDef = {
      u: pmath.cos(la) * range(rng, 8, 14),
      v: pmath.sin(la) * range(rng, 8, 14),
      yaw: la,
      w: 2.6,
      d: 2.6,
      h: range(rng, 8, 11),
      roofH: 1.8,
      kind: "tower",
      roof: "pitched",
      tint: 2,
      seed: Math.floor(rng() * 1e9),
    };
    landmarks.push(mast);
    push(mast);
  }

  const steep = range(rng, 1.7, 2.6);
  const tint = rng() < 0.75 ? 0 : 1;
  const depthBase = range(rng, 6.5, 8.5);

  // Road rows — the base fabric every archetype shares: houses lining the
  // through-roads (with optional back rows and garden strips behind).
  const emitRoadRows = (fromR: number, density: number, backRows: boolean): void => {
    for (const a of [roadA, roadB]) {
      const len = edgeAt(a) * range(rng, 0.68, 0.82);
      if (len - fromR < 14) continue;
      for (const side of [-1, 1]) {
        if (rng() < 0.12) continue; // a one-sided stretch
        const off = 2.7 + depthBase / 2 + range(rng, 0.2, 1);
        const rowsN = backRows && rng() < 0.65 ? 2 : 1;
        for (let rI = 0; rI < rowsN; rI++) {
          const o2 = off + rI * (depthBase + 4.2);
          emitHouseRow(rng, push, blocked, {
            x0: pmath.cos(a) * fromR - pmath.sin(a) * o2 * side,
            z0: pmath.sin(a) * fromR + pmath.cos(a) * o2 * side,
            dir: a,
            length: len - fromR,
            face: -side,
            density: density * (rI === 0 ? 1 : 0.8),
            floors: town.tier === "town" && rI === 0 ? 2 : 1,
            tint,
            steep,
            depthBase,
          });
        }
        if (rng() < 0.7) {
          const gl = (len - fromR) * range(rng, 0.45, 0.8);
          const gOff = off + (rowsN - 0.5) * depthBase + 4.5;
          patches.push({
            u: pmath.cos(a) * (fromR + gl / 2) - pmath.sin(a) * gOff * side,
            v: pmath.sin(a) * (fromR + gl / 2) + pmath.cos(a) * gOff * side,
            yaw: a,
            w: gl,
            d: 6,
            tint: 4,
          });
        }
      }
    }
  };
  // Side lanes with their own short rows (towns grow more of them).
  const emitLanes = (n: number): void => {
    for (let i = 0; i < n; i++) {
      const at = range(rng, 0.22, 0.6) * edgeAt(roadA) * (rng() < 0.5 ? 1 : -1);
      const la = roadA + (Math.PI / 2) * (rng() < 0.5 ? 1 : -1) + range(rng, -0.2, 0.2);
      const lx = pmath.cos(roadA) * at;
      const lz = pmath.sin(roadA) * at;
      const ll = town.tier === "town" ? range(rng, 30, 65) : range(rng, 22, 42);
      pushClipped(lx, lz, lx + pmath.cos(la) * ll, lz + pmath.sin(la) * ll, 3.5);
      for (const side of [-1, 1]) {
        if (rng() < 0.3) continue;
        const off = 2 + depthBase / 2;
        emitHouseRow(rng, push, blocked, {
          x0: lx + pmath.cos(la) * 6 - pmath.sin(la) * off * side,
          z0: lz + pmath.sin(la) * 6 + pmath.cos(la) * off * side,
          dir: la,
          length: ll - 8,
          face: -side,
          density: spec.density * 0.85,
          floors: 1,
          tint,
          steep,
          depthBase,
        });
      }
    }
  };

  // --- Fabric by archetype ---
  if (archetype === "row") {
    emitRoadRows(7, Math.min(0.95, spec.density + 0.18), true);
    emitLanes(town.tier === "hamlet" ? 0 : town.tier === "town" ? 2 + Math.floor(rng() * 3) : 1 + Math.floor(rng() * 2));
  } else if (archetype === "green") {
    // An irregular common, houses facing it all around, the well at its heart.
    const gR = plazaR * range(rng, 1.5, 2.1);
    const g2A = rng() * Math.PI * 2;
    parks.push({ u: 0, v: 0, r: gR });
    parks.push({ u: pmath.cos(g2A) * gR * 0.55, v: pmath.sin(g2A) * gR * 0.55, r: gR * 0.7 });
    props.push({ kind: "well", u: range(rng, -3, 3), v: range(rng, -3, 3), s: 1, yaw: rng() * Math.PI * 2 });
    const nArc = 5 + Math.floor(rng() * 3);
    const aOff = rng() * Math.PI * 2;
    for (let i = 0; i < nArc; i++) {
      if (rng() < 0.18) continue; // a missing side — greens are never closed
      const am = aOff + (i / nArc) * Math.PI * 2;
      const rr = gR + 3 + depthBase / 2 + range(rng, 0, 2);
      const arcLen = ((Math.PI * 2 * rr) / nArc) * 0.72;
      const dir = am + Math.PI / 2 + range(rng, -0.15, 0.15);
      emitHouseRow(rng, push, blocked, {
        x0: pmath.cos(am) * rr - pmath.cos(dir) * (arcLen / 2),
        z0: pmath.sin(am) * rr - pmath.sin(dir) * (arcLen / 2),
        dir,
        length: arcLen,
        face: -1, // doors to the green
        density: spec.density,
        floors: town.tier === "town" ? 2 : 1,
        tint,
        steep,
        depthBase,
      });
    }
    // A second loose ring for towns.
    if (town.tier === "town") {
      for (let i = 0; i < nArc; i++) {
        if (rng() < 0.35) continue;
        const am = aOff + ((i + 0.5) / nArc) * Math.PI * 2;
        const rr = gR + depthBase * 2 + range(rng, 9, 14);
        const arcLen = ((Math.PI * 2 * rr) / nArc) * 0.66;
        const dir = am + Math.PI / 2 + range(rng, -0.18, 0.18);
        emitHouseRow(rng, push, blocked, {
          x0: pmath.cos(am) * rr - pmath.cos(dir) * (arcLen / 2),
          z0: pmath.sin(am) * rr - pmath.sin(dir) * (arcLen / 2),
          dir,
          length: arcLen,
          face: -1,
          density: spec.density * 0.8,
          floors: 1,
          tint,
          steep,
          depthBase,
        });
      }
    }
    // Beyond the green, the through-roads are lined like any street village.
    emitRoadRows(gR + depthBase + 6, spec.density, town.tier === "town");
    if (town.tier === "town") emitLanes(1 + Math.floor(rng() * 2));
  } else if (archetype === "ring") {
    // Bent concentric rows — the nucleated village.
    const gap = range(rng, 15.5, 19);
    const lim = edgeAt(roadA) * 0.88;
    for (let r = plazaR + 6; r + gap * 0.6 < lim; r += gap) {
      const midR = r + gap / 2;
      const nB = Math.max(3, Math.round((Math.PI * 2 * midR) / range(rng, 26, 36)));
      for (let i = 0; i < nB; i++) {
        const am = (i / nB) * Math.PI * 2 + range(rng, -0.03, 0.03);
        const wob = range(rng, -0.09, 0.09);
        const dir = am + Math.PI / 2 + wob;
        const arcLen = ((Math.PI * 2 * midR) / nB) * 0.8;
        emitHouseRow(rng, push, blocked, {
          x0: pmath.cos(am) * midR - pmath.cos(dir) * (arcLen / 2),
          z0: pmath.sin(am) * midR - pmath.sin(dir) * (arcLen / 2),
          dir,
          length: arcLen,
          face: r === plazaR + 6 ? -1 : rng() < 0.5 ? 1 : -1,
          density: spec.density,
          floors: town.tier === "town" && r < plazaR + 6 + gap ? 2 : 1,
          tint,
          steep,
          depthBase,
        });
      }
    }
    props.push({ kind: "well", u: range(rng, -2, 2), v: range(rng, -2, 2), s: 1, yaw: rng() * Math.PI * 2 });
  } else if (archetype === "crossroads") {
    // Two roads, four corner knots.
    const roadC = roadA + Math.PI / 2 + range(rng, -0.25, 0.25);
    const cl = edgeAt(roadC) * 0.7;
    pushClipped(-pmath.cos(roadC) * cl, -pmath.sin(roadC) * cl, pmath.cos(roadC) * cl, pmath.sin(roadC) * cl, 4);
    for (const a of [roadA, roadC]) {
      for (const side of [-1, 1]) {
        if (rng() < 0.3) continue;
        const off = 2.6 + depthBase / 2 + range(rng, 0.2, 0.8);
        const start = range(rng, 5, 9);
        emitHouseRow(rng, push, blocked, {
          x0: pmath.cos(a) * start - pmath.sin(a) * off * side,
          z0: pmath.sin(a) * start + pmath.cos(a) * off * side,
          dir: a,
          length: range(rng, 16, 30),
          face: -side,
          density: spec.density,
          floors: 1,
          tint,
          steep,
          depthBase,
        });
      }
    }
  } else {
    // cluster: a farmstead knot around a dirt yard.
    patches.push({ u: 0, v: 0, yaw: rng() * Math.PI * 2, w: range(rng, 14, 20), d: range(rng, 12, 16), tint: 3 });
    const n = town.tier === "outpost" ? 4 + Math.floor(rng() * 3) : 5 + Math.floor(rng() * 4);
    const aOff = rng() * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const am = aOff + (i / n) * Math.PI * 2 + range(rng, -0.35, 0.35);
      const rr = range(rng, 9, 18);
      const barn = rng() < 0.25;
      const b: BuildingDef = {
        u: pmath.cos(am) * rr,
        v: pmath.sin(am) * rr,
        yaw: am + Math.PI / 2 + range(rng, -0.3, 0.3),
        w: barn ? range(rng, 8, 11) : range(rng, 4.5, 7),
        d: barn ? range(rng, 5.5, 7) : range(rng, 4.5, 6),
        h: barn ? range(rng, 4, 5) : range(rng, 3, 4),
        roofH: range(rng, 1.5, 2.3),
        kind: barn ? "barn" : "dwelling",
        roof: "pitched",
        tint,
        seed: Math.floor(rng() * 1e9),
      };
      if (!blocked(b)) push(b);
    }
    if (rng() < 0.5) props.push({ kind: "well", u: range(rng, -2, 2), v: range(rng, -2, 2), s: 1, yaw: rng() * Math.PI * 2 });
  }

  // Sparse lamps: one side of the main road, a couple at the heart.
  for (let d = 12; d < edgeAt(roadA) * 0.7 && lamps.length < 14; d += range(rng, 18, 26)) {
    lamps.push({ u: pmath.cos(roadA) * d - pmath.sin(roadA) * 3.6, v: pmath.sin(roadA) * d + pmath.cos(roadA) * 3.6 });
  }
  lamps.push({ u: range(rng, -4, 4), v: range(rng, -4, 4) });

  emitFarmBelt(town, rng, patches, props, push);

  return {
    plazaR,
    plazaU: 0,
    plazaV: 0,
    streetW: STREET_W,
    plan: "rings",
    spokes: [],
    rings: [],
    streets,
    parks,
    patches,
    props,
    stubs: town.fingers.map((f) => f.a),
    buildings,
    lamps,
    maxH,
    cellSize: CELL_SIZE,
    cells: new Map(),
  };
}

// ---------------------------------------------------------------------------
//
// Pipeline (each stage seeded, so every city is a distinct pure function):
//  1. IDENTITY  — plan archetype (grid / radial / hybrid), axis orientation,
//     plaza size + offset, palette lean, height of the core skyline.
//  2. DISTRICTS — Voronoi sites typed core / market / residential / yards /
//     park. District drives density, height multiplier, wall palette, kinds.
//  3. BLOCKS    — the plan emits street ribbons and block rectangles (grid
//     cells, or annular arcs chunked into blocks); one shared lot-filler
//     turns blocks into rows of varied buildings, with occupancy falloff
//     toward the outskirts and deliberate park/courtyard gaps.
//  4. LANDMARKS — plaza spire, domed hall, vista spire on the main axis —
//     placed first so the fabric parts around them.
//
// Height model: the skyline peaks at the core (bid-rent intuition) and
// decays as a gaussian of distance — flat-roof towers downtown, pitched
// low-rise at the edge, big flat warehouses in the yards.

interface DistrictSite {
  u: number;
  v: number;
  type: "core" | "market" | "res" | "yards" | "park";
}

function cityLayout(town: TownDef, rng: Rng): TownLayout {
  const padR = town.padR;
  const buildable = padR; // the lobed edge (townRadiusAt) is the real bound

  // --- 1. Identity ---
  const plan = pick(rng, ["grid", "radial", "hybrid", "twin"] as const);
  const axis = rng() * Math.PI;
  const plazaR = range(rng, 26, 40);
  // The core is ALWAYS off-center (the exact-center onion is the #1 tell).
  const plazaOff = plan === "grid" || plan === "twin" ? range(rng, 20, padR * 0.2) : range(rng, 12, 70);
  const plazaA = rng() * Math.PI * 2;
  const plazaU = pmath.cos(plazaA) * plazaOff;
  const plazaV = pmath.sin(plazaA) * plazaOff;
  // Silhouette archetype: ONE dominant vertical (the "weenie") + quantized
  // storey bands — crafted skylines are datum + spikes, never a gradient.
  const silhouette = pick(rng, ["spire", "dome", "twins", "keep"] as const);
  const walled = rng() < (silhouette === "keep" ? 1 : 0.7);
  // The historic core: tight bent fabric around the plaza, wrapped by the
  // (outgrown) wall; the planned extension grafts on outside it.
  const oldR = buildable * range(rng, 0.26, 0.36);
  const coreBand = 4 + Math.floor(rng() * 2); // extension-core storey datum
  const eraShade = rng() < 0.5 ? 0 : 1; // extension palette family

  // --- 2. Districts (extension character only — the old core is its own) ---
  const sites: DistrictSite[] = [{ u: plazaU, v: plazaV, type: "core" }];
  const K = 6 + Math.floor(rng() * 4);
  for (let i = 1; i < K; i++) {
    const a = (i / (K - 1)) * Math.PI * 2 + range(rng, -0.45, 0.45);
    const r = buildable * range(rng, 0.32, 0.85);
    sites.push({ u: plazaU + pmath.cos(a) * r, v: plazaV + pmath.sin(a) * r, type: "res" });
  }
  let nearI = 1;
  let farI = 1;
  for (let i = 2; i < K; i++) {
    const di = pmath.hypot(sites[i].u - plazaU, sites[i].v - plazaV);
    if (di < pmath.hypot(sites[nearI].u - plazaU, sites[nearI].v - plazaV)) nearI = i;
    if (di > pmath.hypot(sites[farI].u - plazaU, sites[farI].v - plazaV)) farI = i;
  }
  sites[nearI].type = "market";
  if (farI !== nearI) sites[farI].type = "yards";
  const parkCount = 1 + (rng() < 0.5 ? 1 : 0);
  for (let n = 0, i = 1; i < K && n < parkCount; i++) {
    if (sites[i].type === "res" && rng() < 0.5) {
      sites[i].type = "park";
      n++;
    }
  }
  const districtOf = (u: number, v: number): DistrictSite => {
    let best = sites[0];
    let bd = Infinity;
    for (const s of sites) {
      const du = u - s.u;
      const dv = v - s.v;
      const d2 = du * du + dv * dv;
      if (d2 < bd) {
        bd = d2;
        best = s;
      }
    }
    return best;
  };

  const buildings: BuildingDef[] = [];
  const parks: { u: number; v: number; r: number }[] = [];
  const patches: GroundPatch[] = [];
  const props: TownProp[] = [];
  const streets: StreetSeg[] = [];
  const spokes: number[] = [];
  const rings: number[] = [];
  const lamps: { u: number; v: number }[] = [];
  let maxH = 0;
  const landmarks: BuildingDef[] = [];

  const push = (b: BuildingDef): void => {
    buildings.push(b);
    const top = b.h + b.roofH;
    if (top > maxH) maxH = top;
  };
  const inPlaza = (u: number, v: number, halfDiag: number): boolean =>
    pmath.hypot(u - plazaU, v - plazaV) < plazaR + 4 + halfDiag;
  const clashesLandmark = (b: BuildingDef): boolean => {
    for (const l of landmarks) if (rectsOverlap(b, l, 2)) return true;
    return false;
  };

  // --- 3. The weenie + civic anchors (placed first; fabric parts around) ---
  const weenieA = rng() * Math.PI * 2;
  const mkLandmark = (b: BuildingDef): void => {
    landmarks.push(b);
    push(b);
  };
  let weenieH = 0;
  if (silhouette === "spire") {
    const h = range(rng, 52, 68);
    weenieH = h;
    mkLandmark({
      u: plazaU + pmath.cos(weenieA) * (plazaR + 10),
      v: plazaV + pmath.sin(weenieA) * (plazaR + 10),
      yaw: weenieA + Math.PI / 2,
      w: range(rng, 6, 7.5), d: range(rng, 6, 7.5), h,
      roofH: range(rng, 10, 14), kind: "spire", roof: "pitched", tint: 2,
      seed: Math.floor(rng() * 1e9),
    });
  } else if (silhouette === "dome") {
    const w = range(rng, 20, 26);
    weenieH = range(rng, 17, 21) + w * 0.5;
    mkLandmark({
      u: plazaU + pmath.cos(weenieA) * (plazaR + 8 + w / 2),
      v: plazaV + pmath.sin(weenieA) * (plazaR + 8 + w / 2),
      yaw: weenieA + Math.PI / 2,
      w, d: w, h: weenieH - w * 0.5,
      roofH: w * 0.5, kind: "dome", roof: "flat", tint: 1,
      seed: Math.floor(rng() * 1e9),
    });
  } else if (silhouette === "twins") {
    const h = range(rng, 40, 48);
    weenieH = h;
    const tw = range(rng, 7.5, 8.5);
    for (const s of [-1, 1]) {
      mkLandmark({
        u: plazaU + pmath.cos(weenieA) * (plazaR + 9) - pmath.sin(weenieA) * s * (tw / 2 + 3.2),
        v: plazaV + pmath.sin(weenieA) * (plazaR + 9) + pmath.cos(weenieA) * s * (tw / 2 + 3.2),
        yaw: weenieA + Math.PI / 2,
        w: tw, d: tw, h,
        roofH: range(rng, 4, 6), kind: "spire", roof: "pitched", tint: 2,
        seed: Math.floor(rng() * 1e9),
      });
    }
  } else {
    // keep: a broad squat citadel tower — always walled.
    const h = range(rng, 26, 32);
    weenieH = h;
    mkLandmark({
      u: plazaU + pmath.cos(weenieA) * (plazaR + 12),
      v: plazaV + pmath.sin(weenieA) * (plazaR + 12),
      yaw: weenieA + Math.PI / 2,
      w: range(rng, 14, 18), d: range(rng, 12, 15), h,
      roofH: 0.5, kind: "tower", roof: "flat", tint: 2,
      seed: Math.floor(rng() * 1e9),
    });
  }
  const hallA = weenieA + Math.PI + range(rng, -0.5, 0.5);
  const hallD = range(rng, 11, 15);
  mkLandmark({
    u: plazaU + pmath.cos(hallA) * (plazaR + 5 + hallD / 2),
    v: plazaV + pmath.sin(hallA) * (plazaR + 5 + hallD / 2),
    yaw: hallA + Math.PI / 2,
    w: range(rng, 16, 24), d: hallD, h: range(rng, 8, 11),
    roofH: 0.4, kind: "hall", roof: "flat", tint: 2,
    seed: Math.floor(rng() * 1e9),
  });
  // Secondary landmark: ≤ half the weenie, out along the FIRST exit road
  // (a terminated vista on the approach).
  if (rng() < 0.55 && town.fingers.length > 0) {
    const va = town.fingers[0].a;
    const vd = townRadiusAt(town, va) * range(rng, 0.62, 0.8);
    mkLandmark({
      u: pmath.cos(va) * vd, v: pmath.sin(va) * vd, yaw: va,
      w: range(rng, 4, 5.2), d: range(rng, 4, 5.2), h: weenieH * range(rng, 0.38, 0.5),
      roofH: range(rng, 4, 7), kind: "spire", roof: "pitched", tint: 2,
      seed: Math.floor(rng() * 1e9),
    });
  }

  // --- 4. Fabric: quantized bands, burgage rows, coherent jitter groups ---
  const DISTRICT_DENSITY: Record<DistrictSite["type"], number> = {
    core: 0.92, market: 0.95, res: 0.82, yards: 0.72, park: 0,
  };
  const BAND: Record<DistrictSite["type"], number> = {
    core: coreBand, market: 1, res: 2, yards: 1, park: 0,
  };
  const DISTRICT_TINT: Record<DistrictSite["type"], number> = {
    core: 2, market: 1, res: eraShade, yards: 1, park: 0,
  };
  let anomalyLeft = 1; // exactly ONE blue house per city

  const fillBlock = (cu: number, cv: number, yaw: number, bw: number, bd: number, era: "old" | "new"): void => {
    const distT = pmath.hypot(cu, cv); // town-center distance (containment)
    const th = pmath.atan2(cv, cu);
    const edgeR = townRadiusAt(town, th);
    const halfDiag = pmath.hypot(bw, bd) / 2;
    if (distT + halfDiag * 0.75 > edgeR * 0.97) return; // beyond the lobed edge
    const distP = pmath.hypot(cu - plazaU, cv - plazaV);
    if (era === "new" && distP < oldR + 5 + halfDiag * 0.75) return; // old core owns this
    // The wall ring is a keep-out band too (blocks otherwise build through it).
    if (walled && era === "new") {
      const thP = pmath.atan2(cv - plazaV, cu - plazaU);
      const wr = (oldR + 8) * (townRadiusAt(town, thP) / padR) * 0.96 + 6;
      if (Math.abs(distP - wr) < halfDiag * 0.75 + 5) return;
    }
    // The exit roads cut corridors through EVERY fabric (they run at their
    // own azimuths, not the lattice's) — blocks part around them.
    for (const f of town.fingers) {
      const ex = pmath.cos(f.a);
      const ez = pmath.sin(f.a);
      const px = cu - plazaU;
      const pz = cv - plazaV;
      if (px * ex + pz * ez > plazaR * 0.5) {
        const perp = Math.abs(px * ez - pz * ex);
        if (perp < 5.5 + halfDiag * 0.55) return;
      }
    }
    const site = districtOf(cu, cv);
    const type =
      era === "old"
        ? "core"
        : site.type === "market" && pmath.hypot(cu - site.u, cv - site.v) > 95
          ? "res"
          : site.type;
    if (era === "new" && (type === "park" || rng() < 0.05)) {
      if (Math.min(bw, bd) > 14) parks.push({ u: cu, v: cv, r: Math.min(bw, bd) * 0.44 });
      return;
    }
    const cy = pmath.cos(yaw);
    const sy = pmath.sin(yaw);
    const toWorld = (lx: number, lz: number): [number, number] => [
      cu + lx * cy - lz * sy,
      cv + lx * sy + lz * cy,
    ];
    if (era === "new" && type === "yards") {
      // Industry: long uniform sheds sharing ONE yaw — uniformity is CORRECT
      // here; the contrast with the organic fabric is the point.
      const n = bw > 32 && rng() < 0.55 ? 2 : 1;
      for (let i = 0; i < n; i++) {
        const w = bw / n - range(rng, 3, 5);
        const d = bd - range(rng, 4, 6);
        if (w < 8 || d < 8) continue;
        const [u, v] = toWorld((i - (n - 1) / 2) * (bw / n), 0);
        const b: BuildingDef = {
          u, v, yaw, w, d, h: range(rng, 4, 6.5), roofH: 0.3,
          kind: "warehouse", roof: "flat", tint: 1,
          seed: Math.floor(rng() * 1e9),
        };
        if (!inPlaza(u, v, pmath.hypot(w, d) / 2) && !clashesLandmark(b)) push(b);
      }
      return;
    }

    // Edge feather: density thins over the last 20% of radius (content that
    // respects the boundary reads as `if (dist < R)`).
    const q = distT / edgeR;
    const feather = q > 0.8 ? 1 - ((q - 0.8) / 0.2) * 0.7 : 1;
    const density = DISTRICT_DENSITY[type] * feather;
    const market = era === "new" && type === "market";
    // Coherent variation: the BLOCK agrees with itself and disagrees with
    // its neighbors (storey jitter, palette lean, roof steepness).
    const blockJit = [-1, 0, 0, 0, 1][Math.floor(rng() * 5)];
    const blockTint = rng() < 0.15 ? Math.floor(rng() * 3) : era === "old" ? 0 : DISTRICT_TINT[type];
    const steep = era === "old" ? range(rng, 1.9, 2.8) : range(rng, 1.2, 2.0);

    // Burgage rows: houses ON the street line (block edges), party-wall runs
    // with an occasional wide break, ragged BACK line, empty interior.
    const depthBase = era === "old" ? range(rng, 7, 10) : range(rng, 8, 13);
    const rows = bd >= 20 ? 2 : 1;
    for (let row = 0; row < rows; row++) {
      const rowSign = rows === 2 ? (row === 0 ? -1 : 1) : rng() < 0.5 ? -1 : 1;
      let x = -bw / 2 + 1.2;
      let run = 3 + Math.floor(rng() * 7);
      while (x + 4 < bw / 2 - 1.2) {
        const lotW = market ? range(rng, 5.5, 8.5) : era === "old" ? range(rng, 4.5, 8) : range(rng, 6, 11);
        const gap = run > 0 ? range(rng, 0.15, 0.6) : range(rng, 5, 13);
        if (run <= 0) run = 3 + Math.floor(rng() * 7);
        run--;
        const occupied = rng() < density;
        const depthRaw = Math.min(bd / rows - 1.5, depthBase + range(rng, -1.5, 1.5));
        const lotSeed = Math.floor(rng() * 1e9);
        const rare = rng();
        // 1-in-15: a building that predates the street — skewed, inset,
        // and ISOLATED (its rotated corners need clearance both sides).
        const skewed = rare < 1 / 15;
        const skew = skewed ? range(rng, 0.35, 0.6) * (rng() < 0.5 ? 1 : -1) : 0;
        if (skewed) x += 2;
        const w = Math.min(skewed ? Math.min(lotW, 6.5) * 0.85 : lotW, bw / 2 - 1.2 - x);
        const cxLocal = x + w / 2;
        x += w + gap + (skewed ? 3.2 : 0);
        const depth = skewed ? depthRaw * 0.8 : depthRaw;
        if (w < 3.8 || !occupied) continue;
        let floors = Math.max(1, BAND[type] + blockJit + (rare > 0.94 ? 1 : 0));
        if (market) floors = 1;
        const h = market ? range(rng, 2.7, 4.2) : floors * 2.95 + range(rng, 0.5, 0.9);
        const flat = era === "new" && (floors >= 4 || (floors === 3 && rng() < 0.4));
        // Front line sits ON the block edge (zero setback — the street wall).
        const lz = rowSign * (bd / 2 - depth / 2 - 0.2);
        const [u, v] = toWorld(cxLocal, lz - rowSign * (skewed ? 2.4 : 0));
        const mk = (uu: number, vv: number, ww: number, ff: number, sd: number): BuildingDef => ({
          u: uu, v: vv, yaw: yaw + skew,
          w: ww, d: depth, h: market ? h : ff * 2.95 + range(rng, 0.5, 0.9),
          roofH: flat ? 0.3 : steep * Math.min(1, ww / 7),
          kind: market ? "market" : flat && floors >= 4 ? "tower" : "dwelling",
          roof: flat ? "flat" : "pitched",
          tint: anomalyLeft > 0 && rare > 0.999 ? (anomalyLeft--, 3) : blockTint,
          seed: sd,
        });
        if (!market && w > 8 && rng() < 0.2) {
          // Composite: two rects sharing a party wall, different heights —
          // an addition, i.e. TIME, encoded in geometry.
          const w1 = w * range(rng, 0.38, 0.55);
          const f2 = Math.max(1, floors + (rng() < 0.5 ? -1 : 1));
          const [u1, v1] = toWorld(cxLocal - (w - w1) / 2, lz);
          const [u2, v2] = toWorld(cxLocal + w1 / 2 - 0.01, lz);
          const b1 = mk(u1, v1, w1, floors, lotSeed);
          const b2 = mk(u2, v2, w - w1, f2, Math.floor(rng() * 1e9));
          if (!inPlaza(u1, v1, w / 2 + depth) && !clashesLandmark(b1) && !clashesLandmark(b2)) {
            push(b1);
            push(b2);
          }
          continue;
        }
        const b = mk(u, v, w, floors, lotSeed);
        if (!inPlaza(u, v, pmath.hypot(w, depth) / 2) && !clashesLandmark(b)) push(b);
      }
    }
    // Interior garden (the yards behind the street wall).
    if (rows === 2 && bd - 2 * depthBase > 7 && bw > 14 && rng() < 0.75) {
      patches.push({ u: cu, v: cv, yaw, w: bw - 6, d: bd - 2 * depthBase - 3, tint: 4 });
    }
  };

  // --- 5. Streets: High Street through-route + exit roads (from the pad
  // fingers, so roads, gates and boundary lobes all agree) ---
  // Fabric streets must die at the LOBED edge (ribbons marching over the
  // wild terrain read as graph paper). Chunk + filter; only the exit roads
  // cross the farm belt.
  const pushFabricStreet = (u0: number, v0: number, u1: number, v1: number, w: number): void => {
    const len = pmath.hypot(u1 - u0, v1 - v0);
    if (len < 6) return;
    const n = Math.max(1, Math.ceil(len / 24));
    for (let i = 0; i < n; i++) {
      const q0 = i / n;
      const q1 = (i + 1) / n;
      const mu = u0 + (u1 - u0) * ((q0 + q1) / 2);
      const mv = v0 + (v1 - v0) * ((q0 + q1) / 2);
      if (pmath.hypot(mu, mv) > townRadiusAt(town, pmath.atan2(mv, mu)) * 0.985 - 2) continue;
      streets.push({
        u0: u0 + (u1 - u0) * q0,
        v0: v0 + (v1 - v0) * q0,
        u1: u0 + (u1 - u0) * q1,
        v1: v0 + (v1 - v0) * q1,
        w,
      });
    }
  };
  const roadEnd = (a: number): number => townRadiusAt(town, a) + town.farmW * 0.92;
  const emitRoad = (a: number, w: number, fromR: number): void => {
    const end = roadEnd(a);
    const kinkR = fromR + (end - fromR) * range(rng, 0.35, 0.6);
    const bend = range(rng, -0.16, 0.16);
    const k: [number, number] = [
      pmath.cos(a) * kinkR - pmath.sin(a) * bend * kinkR * 0.5,
      pmath.sin(a) * kinkR + pmath.cos(a) * bend * kinkR * 0.5,
    ];
    streets.push({ u0: pmath.cos(a) * fromR + plazaU * (fromR < oldR ? 1 : 0), v0: pmath.sin(a) * fromR + plazaV * (fromR < oldR ? 1 : 0), u1: k[0], v1: k[1], w });
    streets.push({ u0: k[0], v0: k[1], u1: pmath.cos(a) * end, v1: pmath.sin(a) * end, w });
  };
  town.fingers.forEach((f, i) => emitRoad(f.a, i < 2 ? 8.5 : 6, i < 2 ? plazaR * 0.7 : oldR * 0.7));

  // --- 6. The old core: tight bent rings of small houses around the plaza ---
  {
    const gap = range(rng, 19, 25);
    const arcT = range(rng, 20, 30);
    for (let r = plazaR + 9; r + gap * 0.7 < oldR; r += gap) {
      const midR = r + gap / 2 - 1.5;
      const n = Math.max(3, Math.round((Math.PI * 2 * midR) / arcT));
      for (let i = 0; i < n; i++) {
        const am = (i / n) * Math.PI * 2 + range(rng, -0.03, 0.03);
        const wobble = range(rng, -0.07, 0.07); // bent lanes, block by block
        // Chord blocks under-fit the arc AND the wobble sweeps row-end
        // corners — the gap must absorb both or neighbors interpenetrate.
        fillBlock(
          plazaU + pmath.cos(am) * midR,
          plazaV + pmath.sin(am) * midR,
          am + Math.PI / 2 + wobble,
          ((Math.PI * 2 * midR) / n) * 0.86 - 2.5,
          gap - 5.5,
          "old",
        );
      }
    }
  }

  // --- 7. The wall the town outgrew (+ gates where the roads cross it) ---
  if (walled) {
    const wallH = range(rng, 5.5, 7);
    const NV = 12 + Math.floor(rng() * 4);
    const verts: [number, number][] = [];
    for (let i = 0; i < NV; i++) {
      const a = (i / NV) * Math.PI * 2 + range(rng, -0.06, 0.06);
      const wr = (oldR + 8) * (townRadiusAt(town, a) / padR) * 0.96 + 6;
      verts.push([plazaU + pmath.cos(a) * wr, plazaV + pmath.sin(a) * wr]);
    }
    for (let i = 0; i < NV; i++) {
      const [x0, z0] = verts[i];
      const [x1, z1] = verts[(i + 1) % NV];
      const segA = pmath.atan2(z1 - z0, x1 - x0);
      const len = pmath.hypot(x1 - x0, z1 - z0);
      // A gate wherever an exit road crosses this stretch of wall.
      let gateT = -1;
      for (const f of town.fingers) {
        const ex = pmath.cos(f.a);
        const ez = pmath.sin(f.a);
        // Ray plaza→f.a vs segment (2D cross products).
        const d0 = (x0 - plazaU) * ez - (z0 - plazaV) * ex;
        const d1 = (x1 - plazaU) * ez - (z1 - plazaV) * ex;
        if (d0 * d1 < 0) {
          const t = d0 / (d0 - d1);
          const gx = x0 + (x1 - x0) * t;
          const gz = z0 + (z1 - z0) * t;
          if ((gx - plazaU) * ex + (gz - plazaV) * ez > 0) gateT = t;
        }
      }
      const nSeg = Math.max(1, Math.ceil(len / 9));
      for (let s = 0; s < nSeg; s++) {
        const t0 = s / nSeg;
        const t1 = (s + 1) / nSeg;
        if (gateT >= 0 && gateT > t0 - 0.06 && gateT < t1 + 0.06) continue; // the gate gap
        const mx = x0 + (x1 - x0) * ((t0 + t1) / 2);
        const mz = z0 + (z1 - z0) * ((t0 + t1) / 2);
        push({
          u: mx, v: mz, yaw: segA, w: len / nSeg + 0.4, d: 1.3, h: wallH,
          roofH: 0, kind: "wall", roof: "flat", tint: 2,
          seed: Math.floor(rng() * 1e9),
        });
      }
      // Vertex tower (kind "gate" = wall-chain family — deliberate overlaps).
      push({
        u: x0, v: z0, yaw: segA, w: 4.6, d: 4.6, h: wallH + range(rng, 3.5, 5),
        roofH: range(rng, 1.8, 2.6), kind: "gate", roof: "pitched", tint: 2,
        seed: Math.floor(rng() * 1e9),
      });
      // Gate towers flanking the gap.
      if (gateT >= 0) {
        const gx = x0 + (x1 - x0) * gateT;
        const gz = z0 + (z1 - z0) * gateT;
        for (const s of [-1, 1]) {
          push({
            u: gx + pmath.cos(segA) * s * 5.5,
            v: gz + pmath.sin(segA) * s * 5.5,
            yaw: segA, w: 5, d: 5, h: wallH + range(rng, 4.5, 6),
            roofH: range(rng, 2, 2.8), kind: "gate", roof: "pitched", tint: 2,
            seed: Math.floor(rng() * 1e9),
          });
        }
      }
    }
  }

  // --- 8. Extension fabric by plan archetype (outside the old core) ---
  interface GridClip {
    nx: number;
    nz: number;
    side: number;
  }
  const emitGridBlocks = (rMax: number, gAxis: number, clip?: GridClip, plazaFrame = false): void => {
    const baseU = range(rng, 36, 46);
    const baseV = range(rng, 32, 42);
    const ca = pmath.cos(gAxis);
    const sa = pmath.sin(gAxis);
    const gridLines = (base: number): number[] => {
      const out: number[] = [0];
      let acc = 0;
      while (acc < rMax) out.push((acc += base * range(rng, 0.8, 1.3)));
      acc = 0;
      while (acc > -rMax) out.push((acc -= base * range(rng, 0.8, 1.3)));
      return out.sort((a, b) => a - b);
    };
    const us = gridLines(baseU);
    const vs = gridLines(baseV);
    const sideOf = (u: number, v: number): number =>
      clip ? ((u - plazaU) * clip.nx + (v - plazaV) * clip.nz) * clip.side : Infinity;
    const emitLine = (off: number, along: "u" | "v", w: number): void => {
      if (Math.abs(off) >= rMax) return;
      const half = Math.sqrt(rMax * rMax - off * off);
      const [du, dv] = along === "u" ? [ca, sa] : [-sa, ca];
      const [ou, ov] = along === "u" ? [-sa, ca] : [ca, sa];
      let t0 = -half;
      let t1 = half;
      if (clip) {
        const s = (t: number): number => sideOf(plazaU + ou * off + du * t, plazaV + ov * off + dv * t);
        const s0 = s(t0);
        const s1 = s(t1);
        if (s0 < 3 && s1 < 3) return;
        if (s0 < 3 || s1 < 3) {
          const tc = t0 + ((3 - s0) / (s1 - s0)) * (t1 - t0);
          if (s0 < 3) t0 = tc;
          else t1 = tc;
        }
      }
      // Clip out the old-core disc (lines are chords offset |off| from the
      // plaza, so the excluded interval is symmetric around t = 0).
      const emitSpan = (a: number, b: number): void => {
        if (b - a < 10) return;
        pushFabricStreet(
          plazaU + ou * off + du * a,
          plazaV + ov * off + dv * a,
          plazaU + ou * off + du * b,
          plazaV + ov * off + dv * b,
          w,
        );
      };
      const rc = oldR + 2;
      if (Math.abs(off) < rc) {
        const tc = Math.sqrt(rc * rc - off * off);
        emitSpan(t0, Math.min(t1, -tc));
        emitSpan(Math.max(t0, tc), t1);
      } else {
        emitSpan(t0, t1);
      }
    };
    for (let i = 0; i < vs.length; i++) emitLine(vs[i], "u", i % 3 === 0 ? 6.5 : 4.5);
    for (let i = 0; i < us.length; i++) emitLine(us[i], "v", i % 3 === 0 ? 6.5 : 4.5);
    for (let iu = 0; iu < us.length - 1; iu++) {
      for (let iv = 0; iv < vs.length - 1; iv++) {
        const gu = (us[iu] + us[iu + 1]) / 2;
        const gv = (vs[iv] + vs[iv + 1]) / 2;
        const bw = us[iu + 1] - us[iu] - 5.5;
        const bd = vs[iv + 1] - vs[iv] - 5.5;
        if (bw < 14 || bd < 14) continue;
        const u = plazaU + gu * ca - gv * sa;
        const v = plazaV + gu * sa + gv * ca;
        const halfDiag = pmath.hypot(bw, bd) / 2;
        if ((plazaFrame ? pmath.hypot(gu, gv) : pmath.hypot(u, v)) + halfDiag > rMax) continue;
        if (inPlaza(u, v, -halfDiag * 0.4)) continue;
        if (sideOf(u, v) < 3 + halfDiag * 0.5) continue;
        fillBlock(u, v, gAxis, bw, bd, "new");
      }
    }
  };

  const emitRadialBlocks = (rMin: number, rMax: number): void => {
    const gap = range(rng, 30, 38);
    const M = 6 + Math.floor(rng() * 4);
    const spokeBase = rng() * Math.PI * 2;
    const spokeA: number[] = [];
    for (let s = 0; s < M; s++) {
      const a = spokeBase + (s / M) * Math.PI * 2 + range(rng, -0.08, 0.08);
      spokes.push(a);
      spokeA.push(a);
      const du = pmath.cos(a);
      const dv = pmath.sin(a);
      const b = plazaU * du + plazaV * dv;
      const disc = Math.sqrt(Math.max(0, b * b - (plazaU * plazaU + plazaV * plazaV - rMax * rMax)));
      const tEnd = Math.min(rMax, -b + disc);
      pushFabricStreet(
        plazaU + du * Math.max(rMin, plazaR * 0.6),
        plazaV + dv * Math.max(rMin, plazaR * 0.6),
        plazaU + du * tEnd,
        plazaV + dv * tEnd,
        6,
      );
    }
    for (let r = Math.max(rMin, plazaR + 12); r + gap * 0.7 < rMax; r += gap) {
      // Ring streets as CLIPPED chords, not renderer circles — full circles
      // poke through the lobed boundary.
      {
        const rr = r + gap / 2;
        const nCh = Math.max(8, Math.round((Math.PI * 2 * rr) / 22));
        for (let c = 0; c < nCh; c++) {
          const aa0 = (c / nCh) * Math.PI * 2;
          const aa1 = ((c + 1) / nCh) * Math.PI * 2;
          pushFabricStreet(
            plazaU + pmath.cos(aa0) * rr,
            plazaV + pmath.sin(aa0) * rr,
            plazaU + pmath.cos(aa1) * rr,
            plazaV + pmath.sin(aa1) * rr,
            2.8,
          );
        }
      }
      const midR = r + gap / 2 - 2.5;
      const arcTarget = range(rng, 34, 46);
      for (let s = 0; s < M; s++) {
        const a0 = spokeA[s] + 4 / midR;
        const a1 = (s + 1 < M ? spokeA[s + 1] : spokeA[0] + Math.PI * 2) - 4 / midR;
        if (a1 <= a0) continue;
        const arcLen = (a1 - a0) * midR;
        const nBlocks = Math.max(1, Math.round(arcLen / arcTarget));
        for (let bI = 0; bI < nBlocks; bI++) {
          const am = a0 + ((bI + 0.5) / nBlocks) * (a1 - a0);
          const bw = (arcLen / nBlocks) * 0.92;
          const bd = gap - 6;
          fillBlock(plazaU + pmath.cos(am) * midR, plazaV + pmath.sin(am) * midR, am + Math.PI / 2, bw, bd, "new");
        }
      }
    }
  };

  const scanR = padR * townMaxWarp(town) * 0.98;
  if (plan === "grid") emitGridBlocks(scanR, axis);
  else if (plan === "radial") emitRadialBlocks(0, scanR);
  else if (plan === "twin") {
    const seamA = axis + range(rng, 0.9, 2.2);
    const nx = pmath.cos(seamA);
    const nz = pmath.sin(seamA);
    emitGridBlocks(scanR, axis, { nx, nz, side: 1 });
    emitGridBlocks(scanR, axis + range(rng, 0.35, 1.1), { nx, nz, side: -1 });
    const b = plazaU * -nz + plazaV * nx;
    const c = plazaU * plazaU + plazaV * plazaV - scanR * scanR;
    const disc = Math.sqrt(Math.max(0, b * b - c));
    pushFabricStreet(
      plazaU + -nz * (-b - disc),
      plazaV + nx * (-b - disc),
      plazaU + -nz * (-b + disc),
      plazaV + nx * (-b + disc),
      8,
    );
  } else {
    const rGrid = scanR * range(rng, 0.42, 0.52);
    emitGridBlocks(rGrid, axis, undefined, true);
    emitRadialBlocks(rGrid + 8, scanR);
  }

  // --- 9. The working landscape ---
  emitFarmBelt(town, rng, patches, props, push);

  // --- Lamps: plaza ring, then along the wide streets ---
  const plazaLamps = 12 + Math.floor(plazaR / 4);
  for (let i = 0; i < plazaLamps; i++) {
    const a = (i / plazaLamps) * Math.PI * 2 + 0.15;
    lamps.push({ u: plazaU + pmath.cos(a) * (plazaR + 1.4), v: plazaV + pmath.sin(a) * (plazaR + 1.4) });
  }
  for (const s of streets) {
    if (lamps.length > 330) break;
    if (s.w < 6) continue;
    const len = pmath.hypot(s.u1 - s.u0, s.v1 - s.v0);
    const du = (s.u1 - s.u0) / len;
    const dv = (s.v1 - s.v0) / len;
    for (let d = 9; d < len - 4 && lamps.length <= 330; d += 22) {
      lamps.push({ u: s.u0 + du * d + dv * (s.w / 2 + 0.7), v: s.v0 + dv * d - du * (s.w / 2 + 0.7) });
    }
  }

  return {
    plazaR,
    plazaU,
    plazaV,
    streetW: 5,
    plan,
    spokes,
    rings,
    streets,
    parks,
    patches,
    props,
    stubs: town.fingers.map((f) => f.a),
    buildings,
    lamps,
    maxH,
    cellSize: CELL_SIZE,
    cells: new Map(),
  };
}


// ---------------------------------------------------------------------------
// Collision — the game's first walk-blocking props
// ---------------------------------------------------------------------------

/**
 * True when a planet-local point (meters, scalar args) sits inside a
 * building volume expanded by `clearance` — the camera boom's wall test.
 * Same footprints as resolveTownCollision, no mutation.
 */
export function townOccludes(
  sampler: PlanetSampler,
  x: number,
  y: number,
  z: number,
  clearance: number,
): boolean {
  const field = sampler.towns();
  if (field.towns.length === 0) return false;
  const r = pmath.hypot(x, y, z);
  if (r < 1e-6) return false;
  const pad = field.padAt(x / r, y / r, z / r);
  if (pad === null || pad.dist > pad.farmR + clearance + 2) return false;
  const town = pad.town;
  const layout = townLayout(town);
  // Height above the LOCAL pad floor (tangent plane — pad.h carries the sag).
  const height = r - (sampler.def.radius + pad.h * sampler.amplitude);
  if (height > layout.maxH + clearance || height < -2) return false;
  const [t1, t2] = townBasis(town.dir);
  const u = x * t1[0] + y * t1[1] + z * t1[2];
  const v = x * t2[0] + y * t2[1] + z * t2[2];
  const cellIdx = layout.cells.get(
    Math.floor(u / layout.cellSize) * CELL_STRIDE + Math.floor(v / layout.cellSize),
  );
  if (!cellIdx) return false;
  for (const bi of cellIdx) {
    const b = layout.buildings[bi];
    const du = u - b.u;
    const dv = v - b.v;
    const rx = b.w / 2 + clearance + 1;
    const rz = b.d / 2 + clearance + 1;
    if (du * du + dv * dv > rx * rx + rz * rz) continue;
    if (height > b.h + b.roofH + clearance) continue;
    const cy = pmath.cos(b.yaw);
    const sy = pmath.sin(b.yaw);
    const qx = du * cy + dv * sy;
    const qz = -du * sy + dv * cy;
    if (Math.abs(qx) < b.w / 2 + clearance && Math.abs(qz) < b.d / 2 + clearance) return true;
  }
  return false;
}

/**
 * Push a planet-local point (meters) out of any building it overlaps.
 * 2.5-D: footprints are yaw-rotated rectangles on the pad; a point above a
 * building's roof line is ignored (jetpacks fly over towns).
 *
 * `p` is MUTATED laterally; `outPush` (optional) accumulates the applied
 * push so callers can shed velocity into walls. Returns true if pushed.
 * Radial length changes are negligible (pads are flat); callers re-seat to
 * the ground as usual afterward.
 *
 * The applied push is capped at `maxPush` per call, so a body that suddenly
 * finds itself deep inside a footprint (dropping past a roofline) is SHOVED
 * out over several ticks instead of teleporting; ordinary walk-into-wall
 * pushes are centimeters and never hit the cap.
 */
export function resolveTownCollision(
  sampler: PlanetSampler,
  p: [number, number, number],
  capR: number,
  outPush?: [number, number, number],
  maxPush = 0.6,
): boolean {
  const field = sampler.towns();
  if (field.towns.length === 0) return false;
  const r = pmath.hypot(p[0], p[1], p[2]);
  if (r < 1e-6) return false;
  const dx = p[0] / r,
    dy = p[1] / r,
    dz = p[2] / r;
  const pad = field.padAt(dx, dy, dz);
  if (pad === null || pad.dist > pad.farmR + capR + 4) return false;

  const town = pad.town;
  const layout = townLayout(town);
  // Height above the LOCAL pad floor (tangent plane — pad.h carries the sag).
  const height = r - (sampler.def.radius + pad.h * sampler.amplitude);
  if (height > layout.maxH + 0.5) return false; // flying above the rooftops

  const [t1, t2] = townBasis(town.dir);
  // Town-local coordinates (tangent-plane approximation, meters).
  const u = p[0] * t1[0] + p[1] * t1[1] + p[2] * t1[2];
  const v = p[0] * t2[0] + p[1] * t2[1] + p[2] * t2[2];

  const cellIdx = layout.cells.get(
    Math.floor(u / layout.cellSize) * CELL_STRIDE + Math.floor(v / layout.cellSize),
  );
  if (!cellIdx) return false;

  // Occupancy test against every footprint in the cell (party-wall
  // composites make naive per-building pushes oscillate THROUGH the shared
  // wall — an exit must land on genuinely open ground).
  // Standable roofs release the lateral block just below the roofline, so
  // the ground clamp (townStandRadius) can seat feet on top without the
  // wall push fighting it — and a jump can mantle the last 35 cm. Domes and
  // spires stay solid to their tips (curved/pointed tops aren't standable).
  const blockTop = (b: BuildingDef): number =>
    b.kind === "dome" || b.kind === "spire" ? b.h + b.roofH + 0.3 : b.h - 0.35;

  const insideAny = (uu: number, vv: number): boolean => {
    for (const bi of cellIdx) {
      const b = layout.buildings[bi];
      if (height > blockTop(b)) continue;
      const du = uu - b.u;
      const dv = vv - b.v;
      const rx = b.w / 2 + capR;
      const rz = b.d / 2 + capR;
      if (du * du + dv * dv > rx * rx + rz * rz + 2 * (rx + rz) + 2) continue;
      const cy = pmath.cos(b.yaw);
      const sy = pmath.sin(b.yaw);
      const qx = du * cy + dv * sy;
      const qz = -du * sy + dv * cy;
      if (Math.abs(qx) < rx && Math.abs(qz) < rz) return true;
    }
    return false;
  };

  // Find the deepest-penetrated footprint, then take its CHEAPEST exit that
  // is not inside another footprint (the far side of a shared wall).
  let deep: BuildingDef | null = null;
  let deepQx = 0;
  let deepQz = 0;
  let deepPen = -1;
  for (const bi of cellIdx) {
    const b = layout.buildings[bi];
    const du = u - b.u;
    const dv = v - b.v;
    // Euclidean pre-filter — Manhattan bounds under-reach for rotated
    // ELONGATED footprints (warehouses w≫d), leaving corner holes.
    const rx = b.w / 2 + capR + 1;
    const rz = b.d / 2 + capR + 1;
    if (du * du + dv * dv > rx * rx + rz * rz) continue;
    if (height > blockTop(b)) continue; // at/over this roof — not a wall hit
    const cy = pmath.cos(b.yaw);
    const sy = pmath.sin(b.yaw);
    const qx = du * cy + dv * sy;
    const qz = -du * sy + dv * cy;
    const ex = b.w / 2 + capR;
    const ez = b.d / 2 + capR;
    if (Math.abs(qx) >= ex || Math.abs(qz) >= ez) continue;
    const pen = Math.min(ex - Math.abs(qx), ez - Math.abs(qz));
    if (pen > deepPen) {
      deepPen = pen;
      deep = b;
      deepQx = qx;
      deepQz = qz;
    }
  }
  if (!deep) return false;
  const cy = pmath.cos(deep.yaw);
  const sy = pmath.sin(deep.yaw);
  const ex = deep.w / 2 + capR;
  const ez = deep.d / 2 + capR;
  // Four candidate exits, cheapest first — a hair PAST the boundary, or
  // float rounding can leave the point epsilon-inside forever.
  const exits: [number, number, number][] = [
    [ex - deepQx + 1e-3, 1, 0], // +x face
    [ex + deepQx + 1e-3, -1, 0], // −x face
    [ez - deepQz + 1e-3, 0, 1], // +z face
    [ez + deepQz + 1e-3, 0, -1], // −z face
  ];
  exits.sort((a, b) => a[0] - b[0]);
  let pushU = 0;
  let pushV = 0;
  let chosen = false;
  for (const [mag, axx, azz] of exits) {
    const lu = (axx * cy - azz * sy) * mag;
    const lv = (axx * sy + azz * cy) * mag;
    if (!insideAny(u + lu, v + lv)) {
      pushU = lu;
      pushV = lv;
      chosen = true;
      break;
    }
  }
  if (!chosen) {
    // Fully enclosed (shouldn't happen) — take the cheapest exit anyway.
    const [mag, axx, azz] = exits[0];
    pushU = (axx * cy - azz * sy) * mag;
    pushV = (axx * sy + azz * cy) * mag;
  }
  const pl = pmath.hypot(pushU, pushV);
  if (pl > maxPush) {
    pushU *= maxPush / pl;
    pushV *= maxPush / pl;
  }
  const ox = t1[0] * pushU + t2[0] * pushV;
  const oy = t1[1] * pushU + t2[1] * pushV;
  const oz = t1[2] * pushU + t2[2] * pushV;
  p[0] += ox;
  p[1] += oy;
  p[2] += oz;
  if (outPush) {
    outPush[0] += ox;
    outPush[1] += oy;
    outPush[2] += oz;
  }
  return true;
}

export interface TownSegmentHit {
  /** Parametric fraction along p0→p1 of the first contact. */
  t: number;
  /** Surface normal at the contact (planet-local unit vector). */
  n: [number, number, number];
}

/**
 * Sweep a planet-local segment (metres) against a settlement's building
 * volumes — ships and bolts collide with the town, not just the dirt.
 * `inflate` grows footprints by a hull radius (0 for bolts). Buildings
 * are solid boxes to their roof top (hip roofs approximated at 55% of
 * the roof rise). Deterministic and shared: the client's bolts, the
 * flown ship, and the server's authoritative projectiles all agree on
 * where the walls are.
 */
export function townSegmentHit(
  sampler: PlanetSampler,
  p0: [number, number, number],
  p1: [number, number, number],
  inflate = 0,
): TownSegmentHit | null {
  const field = sampler.towns();
  if (field.towns.length === 0) return null;
  const mx = (p0[0] + p1[0]) / 2;
  const my = (p0[1] + p1[1]) / 2;
  const mz = (p0[2] + p1[2]) / 2;
  const mr = pmath.hypot(mx, my, mz);
  if (mr < 1e-6) return null;
  const pad = field.padAt(mx / mr, my / mr, mz / mr);
  if (pad === null || pad.dist > pad.farmR + 60) return null;

  const town = pad.town;
  const layout = townLayout(town);
  const [t1, t2] = townBasis(town.dir);
  const R = sampler.def.radius;
  const amp = sampler.amplitude;
  // Endpoint town coordinates: planar (u, v) via tangent dots; height
  // above the pad PLANE from each endpoint's own pad level (sag included).
  const proj = (p: [number, number, number]): [number, number, number] => {
    const u = p[0] * t1[0] + p[1] * t1[1] + p[2] * t1[2];
    const v = p[0] * t2[0] + p[1] * t2[1] + p[2] * t2[2];
    const r = pmath.hypot(p[0], p[1], p[2]) || 1;
    const pr = field.padAt(p[0] / r, p[1] / r, p[2] / r);
    return [u, v, r - (R + (pr ?? pad).h * amp)];
  };
  const a = proj(p0);
  const bb = proj(p1);
  if (a[2] > layout.maxH + inflate + 2 && bb[2] > layout.maxH + inflate + 2) return null;

  const cs = layout.cellSize;
  const cx0 = Math.floor((Math.min(a[0], bb[0]) - inflate) / cs);
  const cx1 = Math.floor((Math.max(a[0], bb[0]) + inflate) / cs);
  const cz0 = Math.floor((Math.min(a[1], bb[1]) - inflate) / cs);
  const cz1 = Math.floor((Math.max(a[1], bb[1]) + inflate) / cs);
  let bestT = Infinity;
  let bestAxis = 1;
  let bestSign = 1;
  let bestB: BuildingDef | null = null;
  for (let cz = cz0; cz <= cz1; cz++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      const idx = layout.cells.get(cx * CELL_STRIDE + cz);
      if (!idx) continue;
      for (const bi of idx) {
        const b = layout.buildings[bi];
        const cy = pmath.cos(b.yaw);
        const sy = pmath.sin(b.yaw);
        // Segment in the building frame (x along width, z along depth).
        const du0 = a[0] - b.u;
        const dv0 = a[1] - b.v;
        const du1 = bb[0] - b.u;
        const dv1 = bb[1] - b.v;
        const q0x = du0 * cy + dv0 * sy;
        const q0z = -du0 * sy + dv0 * cy;
        const q1x = du1 * cy + dv1 * sy;
        const q1z = -du1 * sy + dv1 * cy;
        const hw = b.w / 2 + inflate;
        const hd = b.d / 2 + inflate;
        const top = b.h + (b.roof === "pitched" ? b.roofH * 0.55 : b.roofH) + inflate;
        // Slab intersection over x/z/height; the bottom is open (buried).
        let tMin = 0;
        let tMax = 1;
        let axis = -1;
        let sign = 1;
        const slab = (s0: number, s1: number, lo: number, hi: number, ax: number): boolean => {
          const d = s1 - s0;
          if (Math.abs(d) < 1e-9) return s0 >= lo && s0 <= hi;
          let te = (lo - s0) / d;
          let tl = (hi - s0) / d;
          let sg = -1;
          if (te > tl) {
            const tt = te;
            te = tl;
            tl = tt;
            sg = 1;
          }
          if (te > tMin) {
            tMin = te;
            axis = ax;
            sign = sg;
          }
          if (tl < tMax) tMax = tl;
          return tMin <= tMax;
        };
        if (!slab(q0x, q1x, -hw, hw, 0)) continue;
        if (!slab(q0z, q1z, -hd, hd, 2)) continue;
        if (!slab(a[2], bb[2], -20, top, 1)) continue;
        if (tMin >= 1 || tMax < 0) continue;
        if (tMin < bestT) {
          bestT = Math.max(0, tMin);
          bestAxis = axis;
          bestSign = sign;
          bestB = b;
        }
      }
    }
  }
  if (!bestB) return null;
  // Contact normal: building-frame axis → tangent frame → planet-local.
  const cy = pmath.cos(bestB.yaw);
  const sy = pmath.sin(bestB.yaw);
  let nu = 0;
  let nv = 0;
  let nh = 0;
  if (bestAxis === 0) {
    nu = cy * bestSign;
    nv = sy * bestSign;
  } else if (bestAxis === 2) {
    nu = -sy * bestSign;
    nv = cy * bestSign;
  } else {
    nh = bestSign;
  }
  const n: [number, number, number] = [
    t1[0] * nu + t2[0] * nv + town.dir[0] * nh,
    t1[1] * nu + t2[1] * nv + town.dir[1] * nh,
    t1[2] * nu + t2[2] * nv + town.dir[2] * nh,
  ];
  const nl = pmath.hypot(n[0], n[1], n[2]) || 1;
  return { t: bestT, n: [n[0] / nl, n[1] / nl, n[2] / nl] };
}

/**
 * Standing surface over the town's buildings at a planet-local position
 * (metres): the highest walkable ROOF under the point as a planet radius,
 * or null on open ground. Flat roofs stand at b.h; pitched hips climb to
 * b.h+roofH at the ridge (matching the renderer's 4-sided cone, base
 * ×1.14); domes and spires aren't standable. Character ground clamps take
 * max(terrain, this) when the feet are at/above the roofline — the lateral
 * wall block in resolveTownCollision releases 0.35 m below the roof so the
 * two never fight.
 */
export function townStandRadius(sampler: PlanetSampler, p: [number, number, number]): number | null {
  const field = sampler.towns();
  if (field.towns.length === 0) return null;
  const r = pmath.hypot(p[0], p[1], p[2]);
  if (r < 1e-6) return null;
  const pad = field.padAt(p[0] / r, p[1] / r, p[2] / r);
  if (pad === null || pad.dist > pad.farmR) return null;

  const town = pad.town;
  const layout = townLayout(town);
  const [t1, t2] = townBasis(town.dir);
  const u = p[0] * t1[0] + p[1] * t1[1] + p[2] * t1[2];
  const v = p[0] * t2[0] + p[1] * t2[1] + p[2] * t2[2];
  const cellIdx = layout.cells.get(
    Math.floor(u / layout.cellSize) * CELL_STRIDE + Math.floor(v / layout.cellSize),
  );
  if (!cellIdx) return null;

  let top = 0;
  for (const bi of cellIdx) {
    const b = layout.buildings[bi];
    if (b.kind === "dome" || b.kind === "spire") continue;
    const du = u - b.u;
    const dv = v - b.v;
    const rx = b.w / 2;
    const rz = b.d / 2;
    if (du * du + dv * dv > rx * rx + rz * rz + 2 * (rx + rz) + 2) continue;
    const cy = pmath.cos(b.yaw);
    const sy = pmath.sin(b.yaw);
    const qx = du * cy + dv * sy;
    const qz = -du * sy + dv * cy;
    if (Math.abs(qx) > rx || Math.abs(qz) > rz) continue;
    let h = b.h;
    if (b.roof === "pitched" && b.roofH > 0) {
      // Hip pyramid over the ×1.14 base: unit height 1 − 2·max(|x|,|z|).
      const k = Math.max(Math.abs(qx) / (0.57 * b.w), Math.abs(qz) / (0.57 * b.d));
      h += b.roofH * Math.max(0, 1 - k);
    }
    if (h > top) top = h;
  }
  if (top <= 0) return null;
  return sampler.def.radius + pad.h * sampler.amplitude + top;
}
